// src/lib/i18n/messages/fr.ts
// Le dictionnaire de référence : ses clés sont celles de toutes les langues.
// Voir src/lib/i18n/t.ts pour les deux mécanismes (« {variable} » et pluriels).
//
// Rangé par surface, pas par mot : un même mot peut se traduire différemment selon l'endroit
// (« Engagements » onglet, « engagements » décompte), et une clé par usage laisse le
// traducteur choisir.

import { AI_DISCLOSURE_LONG } from '@/lib/legal';
import { MANIPULATION_PROFILE_DESCRIPTIONS, MANIPULATION_PROFILE_LABELS } from '@/lib/bs-labels-tables';

export const fr = {
  commun: {
    accueil: 'Accueil',
    acteurs: 'Acteurs',
    brouillon: 'Brouillon',
    surDix: '{note}/10',
    // Les guillemets d'un titre cité dans une phrase, à l'usage de la langue.
    citation: '« {texte} »',
    laSource: 'la source',
    ouvrirSource: 'Ouvrir la source',
    sansAdresse: 'sans adresse',
    nonEvalueCourt: 'n.é.',
    essentiel: 'L’essentiel en 30 secondes',
    portraitDe: 'Portrait de {nom}',
    photo: 'Photo',
  },

  ia: {
    // Le texte légal reste dans src/lib/legal.ts : une seule version française fait foi.
    long: AI_DISCLOSURE_LONG,
    court: 'Analyse produite avec l’assistance de l’IA et relue par la rédaction.',
    methodologie: 'Méthodologie',
    signaler: 'Signaler une erreur / droit de réponse',
  },

  // Le bandeau d'un contenu servi dans une autre langue que celle où il a été écrit.
  traduction: {
    auto: 'Traduit automatiquement du français.',
    original: 'Lire l’original',
    aRelire: 'Traduction à relire : visible des seuls admins tant qu’elle n’est pas publiée.',
    perimee: 'Traduction périmée : le français a changé depuis. Visible des seuls admins.',
    absente: 'Ce contenu n’est pas encore traduit : il s’affiche dans sa langue d’origine.',
    section: 'Cette rubrique n’est disponible qu’en français pour le moment.',
  },

  pdf: {
    reserveAbonnes: 'L’export PDF est réservé aux abonnés',
    abonnes: 'PDF · abonnés',
    telecharger: 'Télécharger cette analyse en PDF, à votre nom',
    preparation: 'Préparation…',
    exporter: 'Exporter en PDF',

    // Le document exporté (src/lib/export/, components/export/DocumentImprimable.tsx).
    doc: {
      sommaire: 'Sommaire',
      sources: 'Sources',
      source: 'Source',
      sourceSansTitre: 'Source sans titre',
      version: 'version {n}',
      brouillon: 'brouillon',
      nonPublie: 'non publié',
      filigrane: 'Exporté par {nom} le {date} · Lumière Noire',
      unAbonne: 'un abonné',
      retenir: 'Ce qu’il faut retenir',
      dansCeDocument: 'Dans ce document',
      note: 'Note',
      // Le pied de chaque page : « Baromètre du 11/10/2026, v1 ».
      pied: {
        avecVersion: '{genre} du {date}, v{version}',
        sansVersion: '{genre} du {date}',
        sansDate: '{genre} v{version}',
      },
      genres: {
        analyse: 'Analyse',
        barometre: 'Baromètre',
        rayonx: 'RayonX',
        organisation: 'Fiche',
        acteur: 'Fiche',
      },
      // La ligne d'édition, sous le titre.
      produit: {
        analyse: 'Analyse produite le {date}',
        barometre: 'Baromètre produit le {date}',
        rayonx: 'RayonX produit le {date}',
        organisation: 'Fiche produite le {date}',
        acteur: 'Fiche mise à jour le {date}',
      },
      sansDate: {
        analyse: 'Analyse',
        barometre: 'Baromètre',
        rayonx: 'RayonX',
        organisation: 'Fiche d’organisation',
        acteur: 'Fiche acteur',
      },
      couvertJusquau: 'événements couverts jusqu’au {date}',
      confiance: {
        high: 'confiance élevée',
        medium: 'confiance moyenne',
        low: 'confiance faible',
      },
    },

    analyse: {
      kicker: 'Analyse',
      contexte: 'Contexte',
      perspective: 'Perspective : {label}',
      sansTitre: 'sans titre',
      pointsCles: 'Points clés',
      appuie: 'Ce qui l’appuie',
      contredit: 'Ce qui la contredit',
      limites: 'Ses limites',
      faits: 'Faits établis',
      contestePar: 'Contesté par',
      chronologie: 'Chronologie',
      comparaisons: 'Comparaisons historiques',
      ressemblances: 'Ressemblances',
      differences: 'Différences',
      lecons: 'Leçons',
      scenarios: 'Scénarios',
      probabilite: 'probabilité {n} %',
      probabiliteSuivie: 'probabilité {n} % (à l’analyse : {initiale} %, revue le {date})',
      declencheurs: 'Déclencheurs',
      consequences: 'Conséquences',
      impact: 'Impact citoyen',
      resonance: 'Résonance sociale',
      sentiments: 'Sentiments dominants',
      recitsEmergents: 'Récits émergents',
      manipulation: 'Signaux de manipulation',
    },

    barometre: {
      conclusions: 'Conclusions clés',
      pointsAttention: 'Points d’attention',
      justification: 'Justification de l’évaluation',
      schema: 'Schéma récurrent',
      facteurs: 'Facteurs contextuels',
      engagements: 'Engagements — {domaine}',
      execution: 'exécution {note}/10',
      franchise: 'franchise {note}/10',
      declaration: 'Déclaration — rien à exécuter',
      nonEvaluable: 'Non évaluable (trop vague pour être mesuré)',
      passe: 'Ce qui s’est passé',
      neDisaitPas: 'Ce que la promesse ne disait pas',
      noteFranchise: 'Note de franchise',
      nonTenus: 'Non tenus',
      tenus: 'Tenus',
      partiels: 'Partiellement tenus',
      declarations: 'Déclarations',
      // « non tenu » est un constat d'exécution ; « trahi » un jugement, réservé aux fiches
      // où la donnée ne permet pas le premier (src/lib/bs/notation.ts, titreDuVerdict).
      nonTenusSur: { one: '{n} engagement non tenu sur {total}', other: '{n} engagements non tenus sur {total}' },
      trahisSur: { one: '{n} engagement trahi sur {total}', other: '{n} engagements trahis sur {total}' },
      aucunEvaluable: 'Aucun engagement évaluable',
      executionLibelle: 'Exécution',
      franchiseLibelle: 'Franchise',
      etatDeclaration: 'Déclaration',
      etatNonEvaluable: 'Non évaluable',
      declarationPrecision: 'Rien à exécuter : seule la franchise est notée.',
      nonEvaluablePrecision: 'Trop vague pour être mesuré.',
      methode: 'Deux notes, séparées. L’exécution mesure ce qui a été fait ; la franchise, ce qui a été dit des coûts et des renoncements. Une déclaration n’a que la seconde.',
    },

    rayonx: {
      affirmation: 'L’affirmation',
      verdict: 'Le verdict',
      vrai: 'Ce qui est vrai',
      faux: 'Ce qui est faux ou manque',
      sousAffirmations: 'Les sous-affirmations',
      pointsCles: 'Points clés',
      confirme: 'Ce qui confirme',
      refute: 'Ce qui réfute',
      nuance: 'Ce qui nuance',
      reserves: 'Réserves',
      preuves: 'Les preuves, une par une',
      preuve: 'Preuve {n}',
      score: 'Score du verdict',
      pourOui: 'Preuves pour le oui',
      pourNon: 'Preuves pour le non',
      confirment: 'Preuves qui confirment',
      refutent: 'Preuves qui réfutent',
      nuancent: 'Preuves qui nuancent',
      sansVerdict: 'Sans verdict',
      polarite: {
        confirms: 'confirme',
        refutes: 'réfute',
        nuance: 'nuance',
      },
      poids: {
        p1: 'poids faible',
        p2: 'poids modéré',
        p3: 'poids élevé',
      },
    },

    organisation: {
      kicker: 'Organisation',
      essentiel: 'L’essentiel',
      quoi: 'Qu’est-ce que c’est ?',
      fondee: 'fondée en {date}',
      siege: 'siège : {lieu}',
      budget: 'budget : {montant}',
      ditDElle: 'Ce qu’elle dit d’elle-même',
      poursuit: 'Ce qu’elle poursuit',
      origine: 'D’où elle vient',
      evolution: 'Son évolution',
      pays: 'Pays d’opération',
      qui: 'Qui en fait partie ?',
      categories: {
        politicalLeaders: 'Responsables politiques',
        corporateLeaders: 'Dirigeants d’entreprise',
        financialSector: 'Finance',
        mediaProfessionals: 'Médias',
        academicIntellectuals: 'Universitaires et intellectuels',
        internationalOrganizations: 'Organisations internationales',
        militaryDefense: 'Défense',
      },
      invites: {
        strategicInvitees: 'Invités stratégiques',
        cooptedOpponents: 'Opposants cooptés',
        emergingLeaders: 'Leaders émergents',
      },
      invite: 'invité {annee}',
      pese: 'Comment pèse-t-elle sur les décisions ?',
      recos: 'Recommandations suivies d’effet',
      crises: 'Crises vues venir',
      evoquee: 'évoquée : {date}',
      survenue: 'survenue : {date}',
      rdvDecisions: 'Rendez-vous et décisions',
      rdv: 'Les rendez-vous',
      argent: 'D’où vient l’argent ?',
      quiFinance: 'Qui la finance',
      circuit: 'Un circuit de financement circulaire',
      conflits: 'Conflits d’intérêts',
      transparence: 'Transparence',
      scoreTransparence: 'Score de transparence : {n}/100.',
      travaille: 'Avec qui travaille-t-elle ?',
      orgsLiees: 'Organisations liées',
      membresPartages: '({n} membres partagés)',
      casquettes: 'Membres à plusieurs casquettes',
      reseau: 'Le réseau',
      retenir: 'Que faut-il en retenir ?',
      verdict: 'Verdict',
      constats: 'Constats clés',
      probleme: 'Ce qui pose problème',
      legitimes: 'Contributions légitimes',
      ouvert: 'Qu’est-ce qui reste ouvert ?',
      fiabilite: 'Fiabilité des sources',
      pasEtabli: 'Ce que la fiche n’a pas pu établir',
      pistes: 'Pistes d’enquête',
      membresDocumentes: 'Membres documentés',
      rendezVous: 'Rendez-vous',
    },

    acteur: {
      kicker: 'Fiche acteur',
      biographie: 'Biographie',
      parolesActes: 'Paroles et actes',
      barometreVersion: 'Baromètre, version {n}',
      noteFranchise: 'note de franchise {note}/10',
      conclusions: 'Conclusions clés',
      barometreComplet: 'Le baromètre complet : {url}',
      investigation: 'Investigation',
      investigationComplete: 'L’investigation complète : {url}',
      rayonx: 'RayonX',
      positions: 'Positions publiques',
      position: 'Position',
      appartenances: 'Appartenances',
      relations: 'Relations',
      evenements: 'Événements',
      analyses: 'Dans les analyses',
      organisations: 'Organisations',
      noteDeFranchise: 'Note de franchise',
      essentiel: 'Paroles et actes : {accroche}.',
      roles: {
        speaker: 'intervenant',
        attendee: 'participant',
        organizer: 'organisateur',
        moderator: 'modérateur',
        observer: 'observateur',
      },
    },
  },

  bs: {
    famille: 'Paroles et actes',

    // Les profils : la table de src/lib/bs-labels.ts fait foi pour le français.
    profils: {
      libelles: MANIPULATION_PROFILE_LABELS,
      descriptions: MANIPULATION_PROFILE_DESCRIPTIONS,
    },

    // La règle des promesses (src/components/ds/signes/RegleDesPromesses.tsx).
    regle: {
      noteGlobale: 'note globale {note}',
      contredit: 'contredit par les actes',
      tenu: 'tenu',
    },

    echelle: {
      nonEvalue: 'Non évalué',
      majeurs: 'Écarts majeurs documentés',
      significatifs: 'Écarts significatifs observés',
      partielle: 'Cohérence partielle',
      solide: 'Cohérence solide',
      elevee: 'Cohérence élevée',
    },

    page: {
      introuvable: 'Analyse introuvable',
      voirToutes: '← Voir toutes les analyses',
      angleCible: 'Angle ciblé · {angle}',
      engagementsDe: 'Engagements de {nom}',
      nbEngagements: { one: '{n} engagement', other: '{n} engagements' },
      verdictEnCinq: 'Le verdict en 5 minutes ↓',
      unParUn: { one: 'L’engagement, en détail ↓', other: 'Les {n} engagements, un par un ↓' },
      pied: 'Lumière Noire · Analyse de cohérence · {nom}',
    },

    onglets: {
      verdict: 'Verdict',
      engagements: 'Engagements',
    },

    verdict: {
      repartition: 'Répartition des scores',
      quantifiables: 'Engagements quantifiables',
      qualitatifs: 'Engagements qualitatifs',
      nonEvaluables: {
        one: '{n} engagement non évaluable (formulation insuffisamment précise)',
        other: '{n} engagements non évaluables (formulation insuffisamment précise)',
      },
      conclusions: 'Conclusions clés',
      pointsAttention: 'Points d’attention',
      justification: 'Justification de l’évaluation',
      schemaRecurrent: 'Schéma récurrent — analyse détaillée',
      facteurs: 'Facteurs contextuels',
    },

    liste: {
      avertissement: 'Sélection représentative d’engagements publics. Non exhaustif. Chaque évaluation repose sur des éléments factuels documentés et sourcés.',
      moyenne: 'moy. {note}',
      titreNonEvaluable: 'Engagement trop vague pour être mesuré : non évaluable',
      titreDeclaration: 'Déclaration : rien à exécuter, la note est celle de la franchise',
      badgeDeclaration: 'déclaration · franchise',
    },

    domaines: {
      economie_emploi: 'Économie',
      social_sante: 'Social / Santé',
      securite_immigration: 'Sécurité',
      environnement_energie: 'Environnement',
      international_defense: 'International',
      institutions_democratie: 'Institutions',
    },

    classification: {
      quantifiable: 'quantifiable',
      qualitative: 'qualitative',
      floue: 'floue',
    },

    tenue: {
      tenu: 'Tenu',
      partiel: 'Partiellement tenu',
      non_tenu: 'Non tenu',
    },

    carte: {
      declarationRien: 'Déclaration · rien à exécuter',
      franchise: 'franchise {note}/10',
      deuxAxes: 'exécution {tenue}/10 · franchise {franchise}/10',
      dit: 'CE QUI A ÉTÉ DIT',
      passe: 'CE QUI S’EST PASSÉ',
      neDisaitPas: 'CE QUE LA PROMESSE NE DISAIT PAS',
      sourcesDisent: 'CE QUE DISENT LES SOURCES',
      lecture: 'NOTRE LECTURE',
      passage: 'le passage',
      motPourMot: 'Chaque fait est retrouvé mot pour mot dans sa page.',
      sources: { one: 'La source', other: 'Les {n} sources' },
      analyse: 'L’analyse détaillée',
      poids: 'Comment il pèse dans la note',
      primaireNonEtablie: 'Source primaire non établie : le chiffre ou l’acte n’est cité ici que par des médias, sans l’organisme qui le produit.',
      emetteur: 'Émetteur : {emetteur}',
      sourceN: 'Source {n}',
      quiPaie: 'QUI PAIE RÉELLEMENT',
      payeurs: 'Payeurs : {payeurs} · Bénéficiaires : {beneficiaires}',
      ecartStat: 'ÉCART STATISTIQUE · {gravite}',
      dissonance: 'DISSONANCE OBSERVÉE',
    },

    pastilles: {
      budget: 'Budget',
      couts: 'Coûts',
      clarte: 'Clarté définition',
      coherence: 'Cohérence déclarative',
      stabilite: 'Stabilité temporelle',
      masquer: 'Masquer la raison',
      voir: 'Voir pourquoi',
      pourquoi: '{libelle} : {valeur} — pourquoi',
      // Les valeurs telles que le pipeline les écrit (prompts/bs/step3-analyze.md).
      valeurs: {
        realiste: 'réaliste',
        optimiste: 'optimiste',
        fantaisiste: 'fantaisiste',
        impossible: 'impossible',
        assume: 'assumé',
        minimise: 'minimisé',
        deni: 'déni',
        fiction: 'fiction',
        oui: 'oui',
        floue: 'floue',
        inexistante: 'inexistante',
        coherent: 'cohérent',
        contradictoire: 'contradictoire',
        opportuniste: 'opportuniste',
        stable: 'stable',
        evolutive: 'évolutive',
        reniee: 'reniée',
      },
    },

    notation: {
      deuxMesures: 'Deux mesures, et elles diffèrent',
      tenueTermes: 'Tenue dans ses termes',
      sinceriteReserves: 'Sincérité, réserves payées',
      retientSeconde: 'Le baromètre retient la seconde : atteindre une cible en déplaçant le problème, ou en changeant la règle du comptage, n’est pas la tenir.',
      reservesDocumentees: { one: '{n} réserve documentée', other: '{n} réserves documentées' },
      poidsTitre: 'Poids de cet engagement dans la note · ×{total}',
      pouvoir: 'Pouvoir détenu — {valeur}',
      verifiabilite: 'Vérifiabilité — {valeur}',
      chiffre: 'engagement chiffré',
      appreciable: 'appréciable',
      centralite: 'Centralité — {valeur}',
      nonRetenue: ' · non retenue : {motif}',
      reserves: {
        transfert_de_charge: 'transfert de charge',
        definition_indicateur: 'définition de l’indicateur modifiée',
        perimetre_restreint: 'périmètre restreint',
        causalite_non_reconnue: 'causalité non reconnue',
        financement_non_assume: 'financement non assumé',
      },
      pouvoirs: {
        fonction_et_moyens: 'fonction et moyens détenus',
        fonction_sans_moyens: 'fonction sans les moyens',
        candidat_elu: 'candidat, ensuite élu',
        candidat_battu: 'candidat, non élu',
        aucun_levier: 'aucun levier sur le sujet',
      },
      centralites: {
        programme_repete: 'promesse de programme, répétée',
        engagement_assume: 'engagement public assumé',
        propos_de_circonstance: 'propos de circonstance',
      },
      motifs: {
        moins_de_deux_occurrences_datees: 'moins de deux occurrences datées',
        aucune_occurrence_sourcee: 'aucune occurrence adossée à une adresse',
      },
    },

    // La justification du profil, recalculée à l'affichage (src/lib/bs/profil.ts).
    profil: {
      phrasesReserve: {
        transfert_de_charge: 'une cible atteinte en déplaçant la charge sur quelqu’un d’autre',
        financement_non_assume: 'une dépense annoncée sans dire qui la paie',
        definition_indicateur: 'une cible atteinte après changement de la règle de mesure',
        perimetre_restreint: 'une cible atteinte après réduction de ce qu’elle couvrait',
        causalite_non_reconnue: 'des résultats favorables présentés comme le produit de l’action, sans mention de ce qui y a contribué',
      },
      aucuneReserve: {
        one: 'Sur {n} engagement évaluable, aucune réserve documentée : les écarts relevés sont des contradictions directes, sans transfert de charge, changement de définition ni restriction de périmètre.',
        other: 'Sur {n} engagements évaluables, aucune réserve documentée : les écarts relevés sont des contradictions directes, sans transfert de charge, changement de définition ni restriction de périmètre.',
      },
      avecReserve: {
        one: 'Sur les {total} engagements, {n} s’accompagne d’une réserve.',
        other: 'Sur les {total} engagements, {n} s’accompagnent d’une réserve.',
      },
      laPlusFrequente: 'La plus fréquente, {fois} : {phrase}.',
      ensuite: 'Vient ensuite, {fois}, {phrase}.',
      fois: { one: 'une fois', other: '{n} fois' },
    },
  },

  nav: {
    brief: 'Le Brief',
    analyses: 'Analyses',
    rayonx: 'RayonX',
    acteurs: 'Acteurs',
    organisations: 'Organisations',
    enquetes: 'Enquêtes',
    monQg: 'Mon QG',
    evitement: 'Aller au contenu principal',
    premium: 'Réservé aux abonnés Premium',
    rechercherRaccourci: 'Rechercher (Ctrl+K)',
    rechercherPoints: 'Rechercher…',
    deconnexion: 'Déconnexion',
    connexion: 'Se connecter',
    fermerMenu: 'Fermer le menu',
    menu: 'Menu',
    modeClair: 'Passer en mode clair',
    modeSombre: 'Passer en mode sombre',
  },

  pied: {
    explorer: 'Explorer',
    liensLegaux: 'Liens légaux',
    actu: 'Actu',
    graphe: 'Le Graphe',
    about: 'À propos',
    methodologie: 'Méthodologie',
    mentions: 'Mentions légales',
    confidentialite: 'Confidentialité',
    corrections: 'Corrections & droit de réponse',
    credits: 'Crédits photo',
  },

  recherche: {
    titre: 'Rechercher',
    champ: 'Une personne, une organisation, un sujet…',
    fermer: 'Fermer',
    echap: 'Échap',
    resultats: 'Résultats',
    allerA: 'Aller directement à',
    rien: 'Rien pour « {requete} ».',
    conseil: 'Vérifiez l’orthographe, ou essayez un nom de famille seul.',
    nbResultats: { one: '{n} résultat', other: '{n} résultats' },
    touches: '↑ ↓ pour choisir · Entrée pour ouvrir',
    types: {
      actor: 'Acteurs',
      organization: 'Organisations',
      analysis: 'Analyses',
      investigation: 'Investigations',
    },
    raccourcis: {
      brief: 'Le Brief du jour',
      analyses: 'Les analyses',
      acteurs: 'Les acteurs',
      organisations: 'Les organisations',
      rayonx: 'Les RayonX',
      enquetes: 'Les enquêtes',
    },
  },

  ui: {
    lireLaSuite: 'Lire la suite →',
    reduire: '↑ Réduire',
  },

  suite: {
    titre: 'Et ensuite ?',
    sousTitre: 'La suite du fil, dans d’autres enquêtes.',
    familles: {
      barometre: 'Paroles et actes',
      investigation: 'Investigation',
      organisation: 'Réseau',
      anatomie: 'Anatomie',
      analyse: 'Analyse',
      rayonx: 'RayonX',
    },
  },

  parcours: {
    baroTitre: '{nom} : paroles et actes',
    baroLien: 'Ses engagements confrontés à ce qui a été fait',
    invTitre: 'Investigation : {nom}',
    invLien: 'Parcours, réseaux, zones d’ombre',
    membre: '{nom} en est membre',
    citeeIci: 'Citée dans cette page',
    figure: '{nom} y figure',
    acteurDansAnalyse: '{nom} y est un acteur',
    affirmation: 'Une affirmation sur {nom}, vérifiée',
    orgDansAnalyse: '{nom} y est citée',
  },

  accueil: {
    titreDebut: 'Comprendre pour ne pas',
    titreFin: 'subir.',
    chapeau: 'Entre les médias qui tournent en boucle et les sites qui voient des complots partout, il manquait un espace rigoureux. Des faits sourcés, des contradictions documentées, des réseaux cartographiés. Vos conclusions.',
    lireBrief: 'Lire le Brief du jour →',
    parOuCommencer: 'Par où commencer ?',
    aLaUne: 'À la une',
    aVousDeJuger: 'À vous de juger',
    vedette: 'La vedette du jour',
    tousActeurs: 'Tous les acteurs →',
    tousDossiers: 'Tous les dossiers →',
    trackRecord: 'Tout le track record →',
    voirGraphe: 'Voir le graphe →',
    toutesOrgs: 'Toutes les organisations →',
    precedent: 'Précédent',
    suivant: 'Suivant',
    voirElement: 'Voir {nom}',
    aLirePlusTard: 'À lire plus tard',
    enregistreCourt: 'enregistré pour plus tard',
    enregistreLong: 'ce que vous avez enregistré pour creuser sur grand écran',
    reprendre: 'Reprendre →',
    retirer: 'Retirer',
    bilan: {
      confirmed: { one: 'confirmé', other: 'confirmés' },
      partially_confirmed: { one: 'partiellement', other: 'partiellement' },
      invalidated: { one: 'invalidé', other: 'invalidés' },
      pending: { one: 'en cours', other: 'en cours' },
      sansTri: { one: 'Notre bilan complet, sans tri — {n} scénario invalidé inclus.', other: 'Notre bilan complet, sans tri — {n} scénarios invalidés inclus.' },
    },
    conversion: {
      fermer: 'Fermer',
      badge: 'Compte gratuit · 30 secondes',
      accrocheDebut: 'Lumière Noire va plus loin',
      accrocheFin: 'quand elle vous connaît.',
      sauvegarder: 'Sauvegardez analyses et profils dans Mon QG',
      soumettre: 'Soumettez des sujets, votez pour les suggestions',
      creer: 'Créer un compte gratuit →',
      dejaCompte: 'Déjà un compte ? Se connecter',
    },
  },

  types: {
    acteurs: {
      head_of_state: 'Chef d\'État',
      minister: 'Ministre',
      politician: 'Personnalité politique',
      party_leader: 'Chef de parti',
      diplomat: 'Diplomate',
      corporate_executive: 'Dirigeant d\'entreprise',
      businessman: 'Entrepreneur',
      banker: 'Banquier / Financier',
      lobbyist: 'Lobbyiste',
      military: 'Militaire',
      intelligence: 'Renseignement',
      magistrate: 'Magistrat',
      journalist: 'Journaliste',
      media_owner: 'Propriétaire de média',
      academic: 'Académique / Expert',
      ngo_leader: 'Responsable ONG',
      union_leader: 'Syndicaliste',
      activist: 'Militant / Société civile',
      religious_leader: 'Leader religieux',
      oligarch: 'Oligarque',
      business: 'Entrepreneur',
      company: 'Entreprise',
      institution: 'Institution',
      media: 'Média',
      lobby: 'Lobby',
      ngo: 'Responsable ONG',
    },
    organisations: {
      think_tank: 'Think tank',
      forum: 'Forum',
      club: 'Club / Cercle',
      lobby: 'Lobby / Groupe de pression',
      foundation: 'Fondation',
      ngo: 'ONG',
      union: 'Syndicat',
      political_party: 'Parti politique',
      religious: 'Organisation religieuse',
      corporation: 'Entreprise privée',
      state_company: 'Entreprise publique',
      bank: 'Banque / Institution financière',
      media: 'Groupe médiatique',
      intergovernmental: 'Organisation intergouvernementale',
      military: 'Organisation militaire / Alliance',
      intelligence: 'Service de renseignement',
      university: 'Université / Institution académique',
      unknown: 'Non classifié',
    },
  },

  verdicts: {
    libelles: {
      vrai: 'Vrai',
      'plutôt_vrai': 'Plutôt vrai',
      partiellement_vrai: 'Partiellement vrai',
      'plutôt_faux': 'Plutôt faux',
      faux: 'Faux',
      'non_vérifiable': 'Non vérifiable',
    },
    reponses: {
      vrai: 'Oui',
      'plutôt_vrai': 'Plutôt oui',
      partiellement_vrai: 'En partie',
      'plutôt_faux': 'Plutôt non',
      faux: 'Non',
      'non_vérifiable': 'Impossible à dire',
    },
    issues: {
      confirmed: 'Confirmé',
      partially_confirmed: 'Partiellement confirmé',
      invalidated: 'Invalidé',
      pending: 'En mouvement',
    },
  },

  familles: {
    analyse: 'Analyse',
    barometre: 'Baromètre de cohérence',
    parolesEtActes: 'Paroles et actes',
    rayonx: 'RayonX',
    anatomie: 'Anatomie',
    loupe: 'Loupe',
    investigation: 'Investigation',
    organisation: 'Organisation',
    dossier: 'Dossier',
    acteur: 'Acteur',
    thematique: 'Thématique',
    scenario: 'Scénario',
    declaration: 'Déclaration',
  },

  home: {
    dernieresDepeches: 'Les dernières dépêches',
    leBrief: 'Le Brief →',
    touteActu: 'Toute l’actualité →',
    critique: 'Critique',
    comprendre: 'Comprendre : {famille} {titre} →',
    depuisVisite: 'Depuis votre dernière visite',
    nbDepeches: { one: '{n} dépêche →', other: '{n} dépêches →' },
    nbPublications: { one: '{n} publication', other: '{n} publications' },
    publicationsDepuis: { one: '{n} publication depuis le {date}', other: '{n} publications depuis le {date}' },
    vraiOuFaux: 'Vrai ou faux ?',
    votreAvis: 'Votre avis ?',
    aVotreAvis: 'À votre avis ?',
    bienVu: 'Bien vu.',
    pasCetteFois: 'Pas cette fois.',
    voirVerification: 'Voir la vérification et ses sources →',
    declarationKicker: 'Paroles et actes · Déclaration',
    coherentsQuestion: 'Paroles et actes : cohérents ?',
    voirSesEngagements: 'Voir ses {n} engagements confrontés aux actes →',
    reponses: {
      vrai: 'Vrai',
      nuance: 'Nuancé',
      faux: 'Faux',
      coherents: 'Cohérents',
      en_partie: 'En partie',
      contredits: 'Contredits',
    },
    engagement: {
      coherents: 'Paroles et actes cohérents',
      en_partie: 'Cohérents en partie',
      contredits: 'Paroles contredites par les actes',
    },
    notesGlobales: 'Les notes globales des baromètres',
    questions: {
      promesses: {
        titre: 'Qui tient ses promesses ?',
        texte: 'Les engagements des responsables publics, confrontés à ce qu’ils ont fait.',
      },
      verifications: {
        titre: 'Est-ce vrai que… ?',
        texte: 'Des affirmations qui circulent, passées au crible des sources.',
      },
      reseaux: {
        titre: 'Qui fréquente qui ?',
        texte: 'Ce que sont ces cercles, jusqu’où porte leur influence, et qui y siège.',
      },
      avenir: {
        titre: 'Comment s’y retrouver ?',
        texte: 'Chaque sujet lu sous plusieurs angles, sources à l’appui, jusqu’à ses suites possibles.',
      },
      enquetes: {
        titre: 'Creusons un peu',
        texte: 'Des systèmes, des affaires, des parcours : examinés dans le détail, sources à l’appui.',
      },
    },
    pasIci: 'Ce que vous ne trouverez pas ici : des certitudes habillées en faits, ni des conclusions tirées au-delà de ce que les preuves permettent.',
    notreMethode: 'Lire notre méthode →',
    trackRecord: 'Tout le track record →',
    aBouge: 'Ce qui a bougé',
    scenariosRevusRecemment: 'Scénarios revus récemment',
    scenariosRevus: 'scénarios revus',
    revusFaits: 'Nos scénarios, revus à la lumière des faits',
    enHausse: 'en hausse',
    enBaisse: 'en baisse',
    tuiles: {
      nbEngagementsActes: 'Les {n} engagements confrontés aux actes →',
      lireBarometre: 'Lire le baromètre →',
      leBarometre: 'Le baromètre',
      dossierKicker: 'Dossier · {ancre}',
      metaAnalyse: ' · méta-analyse',
      leDossier: 'Le dossier',
      scenarioKicker: 'Scénario · {sujet}',
      analyseMin: 'analyse',
      lAnalyse: 'L’analyse',
      lAnalyseFleche: 'L’analyse →',
      siegePartout: 'Siège partout',
      organisations: 'organisations',
      leProfil: 'Le profil',
      membresCommun: 'Membres en commun',
      membresCommunsEntre: '{n} membres communs entre {a} et {b}',
      auMoins: 'Au moins {n} personnes siègent aux deux',
      dont: ', dont {noms}',
      deConsensus: 'de consensus',
      consensusLectures: 'Consensus entre les lectures',
      laFiche: 'La fiche',
      lEnquete: 'L’enquête',
      toucherReveler: 'Touchez pour révéler le verdict',
      verdictPreuves: 'Le verdict et ses preuves →',
    },
  },

  decouverte: {
    promesses: '{p} personnalités, {e} engagements confrontés aux actes',
    verifications: { one: '{n} affirmation vérifiée, sources à l\'appui', other: '{n} affirmations vérifiées, sources à l\'appui' },
    reseaux: '{n} organisations analysées',
    reseauxCumul: ', {n} personnes dans plusieurs d’entre elles',
    membresDocumentes: '{nom} · {n} membres documentés',
    sujets: '{n} sujets analysés',
    scenariosDates: ', {n} scénarios datés',
    anatomies: { one: '{n} anatomie de système', other: '{n} anatomies de système' },
    loupes: { one: '{n} loupe sur des affaires', other: '{n} loupes sur des affaires' },
    investigations: { one: '{n} investigation', other: '{n} investigations' },
    enquetesFond: 'enquêtes de fond publiées',
    titreAvenir: '{noms}… comment s’y retrouver ?',
    titreReseaux: '{noms}… c’est quoi, et qui en est ?',
  },

  relie: {
    analysis: 'Analyse',
    investigation: 'Investigation',
    bs: 'Cohérence',
    organisation: 'Cartographie',
  },

  signes: {
    balance: {
      confirme: { one: '{n} confirme', other: '{n} confirment' },
      nuance: { one: '{n} nuance', other: '{n} nuancent' },
      refute: { one: '{n} réfute', other: '{n} réfutent' },
      pourOui: '{n} pour le oui',
      pourNon: '{n} pour le non',
      preuves: '{n} preuves : {detail}',
      voirOui: 'Voir les preuves qui vont vers le oui',
      voirNon: 'Voir les preuves qui vont vers le non',
      voirConfirment: 'Voir les preuves qui confirment',
      voirNuancent: 'Voir les preuves qui nuancent',
      voirRefutent: 'Voir les preuves qui réfutent',
    },
    calendrier: {
      scenariosDates: '{n} scénarios datés',
      point: '{titre} : {p} %, échéance {echeance}',
    },
    constellation: {
      aria: '{n} membres par secteur',
      dont: ', dont {noms}',
    },
    pouls: {
      aria: '{total} dépêches en {semaines} semaines, dont {critiques} critiques',
      semaine: { one: 'Semaine du {debut} : {n} dépêche', other: 'Semaine du {debut} : {n} dépêches' },
      dontCritiques: { one: ', dont {n} critique', other: ', dont {n} critiques' },
      legende: 'Une barre par semaine, à la date de l’événement',
      critiques: 'dont critiques',
    },
    maillage: {
      aria: { one: '{n} personne siège dans plusieurs organisations', other: '{n} personnes siègent dans plusieurs organisations' },
      barre: { one: '{n} personne dans {k} organisations', other: '{n} personnes dans {k} organisations' },
      legende: { one: '{n} personne dans plusieurs organisations, par nombre d’organisations', other: '{n} personnes dans plusieurs organisations, par nombre d’organisations' },
    },
    calendrierCouverture: { one: '{n} scénario daté à venir · plus haut, plus probable', other: '{n} scénarios datés à venir · plus haut, plus probable' },
    presence: {
      aria: 'Présence dans les {total} sous-enquêtes',
      ligne: { one: '{nom} : {n} sous-enquête sur {total}', other: '{nom} : {n} sous-enquêtes sur {total}' },
      legende: 'présence dans les {total} sous-enquêtes',
    },
    regle: 'Règle des promesses',
  },

  temps: {
    aVenir: 'À venir',
    aujourdhui: 'Aujourd\'hui',
    hier: 'Hier',
    ilYa: { one: 'Il y a {n} jour', other: 'Il y a {n} jours' },
  },

  importance: {
    critical: 'Critique',
    high: 'Important',
    medium: 'À noter',
    low: 'Mineur',
  },

  brief: {
    metaTitre: 'Le Brief',
    metaDescription: 'L\'essentiel géopolitique en 3 minutes — dépêches de la semaine, analyses récentes, dernière vérification.',
    kicker: 'Le Brief · {date}',
    titre: 'Trois minutes pour la semaine',
    faitsARetenir: { one: 'fait à retenir · sources à l’appui', other: 'faits à retenir · sources à l’appui' },
    aRetenir: 'À retenir cette semaine',
    semaineCalme: 'Semaine calme : aucun fait majeur relié à nos analyses. Le fil complet est dans',
    lActualite: 'l’actualité',
    pourComprendre: 'Pour comprendre · {famille} :',
    sources: 'Sources',
    cestTout: 'C’est tout pour cette semaine. Besoin de creuser ?',
    touteActu: 'Voir toute l’actualité →',
  },

  portes: {
    publie: 'Ce que nous avons publié',
    nbPublications: { one: '{n} publication sur {nom}', other: '{n} publications sur {nom}' },
    baroDefaut: 'Ses engagements confrontés à ses actes, un par un',
    leBarometre: 'Le baromètre',
    anglesReserves: { one: '{n} autre angle, réservé aux abonnés', other: '{n} autres angles, réservés aux abonnés' },
    voirAngles: 'Voir les angles',
    leDossierDe: 'Le dossier {nom}',
    leDossier: 'Le dossier',
    leVerdict: 'Le verdict →',
  },

  listes: {
    plutotVrais: { one: '{n} plutôt vrai', other: '{n} plutôt vrais' },
    partiellementVrais: { one: '{n} partiellement vrai', other: '{n} partiellement vrais' },
    plutotFaux: { one: '{n} plutôt faux', other: '{n} plutôt faux' },
    verdictsPublies: 'Verdicts publiés : {legende}',
  },

  aria: {
    filAriane: 'Fil d\'Ariane',
  },

  acteur: {
    positions: 'Positions dans les analyses',
    voirPositions: { one: 'Voir {n} position de plus', other: 'Voir {n} positions de plus' },
    positionsPremium: 'Positions dans les analyses réservées aux abonnés Premium',
    apparaitDans: { one: 'Apparaît dans {n} analyse', other: 'Apparaît dans {n} analyses' },
    analyseN: 'Analyse #{n}',
    voirAnalyses: { one: 'Voir {n} analyse de plus', other: 'Voir {n} analyses de plus' },
    pasDansCorpus: 'Cet acteur n\'apparaît pas encore dans les analyses du corpus.',
    aucuneRelation: 'Aucune relation ni participation documentée pour cet acteur.',
    orgInconnue: 'Organisation inconnue',
    inconnu: 'Inconnu',
    nbRelations: { one: '{n} relation', other: '{n} relations' },
    nbOrganisations: { one: '{n} organisation', other: '{n} organisations' },
    nouveautesReseau: 'Nouveautés réseau',
    detecteesRecemment: '· détectées récemment',
    aujourdhui: 'aujourd\'hui',
    hier: 'hier',
    ilYaJ: 'il y a {n} j',
    participations: 'Participations aux événements',
    voirOrganisations: { one: 'Voir {n} organisation de plus', other: 'Voir {n} organisations de plus' },
    nbEvents: { one: '{n} événement', other: '{n} événements' },
    roles: {
      speaker: 'Intervenant',
      attendee: 'Participant',
      organizer: 'Organisateur',
      moderator: 'Modérateur',
      observer: 'Observateur',
    },
    relations: {
      inner_circle: 'Cercle proche',
      political: 'Politique',
      corporate: 'Affaires',
      media: 'Médias',
      international: 'International',
      unexpected: 'Inattendue',
    },
    chargement: 'Chargement…',
    introuvable: 'Acteur introuvable',
    retour: '← Retour aux acteurs',
    acteur: 'Acteur',
    explorerReseau: 'Explorer son réseau dans le graphe →',
    enBref: 'En bref',
    wikipedia: 'Wikipédia ↗',
    affiliations: 'Affiliations',
    frequente: 'Fréquente ces organisations',
    organisationN: 'Organisation #{n}',
    membres: {
      founder: 'Fondateur',
      core_member: 'Membre',
      alumni: 'Alumni',
      young_leader: 'Young Leader',
      invitee: 'Invité',
      honorary: 'Honoraire',
      emerging: 'Émergent',
    },
    onglets: {
      analyses: 'Analyses & enquêtes',
      reseau: 'Réseau',
      contexte: 'Dans nos analyses',
      actualites: 'Actualités',
    },
    analyserEnEntier: 'Faut-il analyser {nom} en entier ?',
    analyserSous: 'Proposez cette analyse au vote — les sujets les plus demandés passent au crible : cohérence des engagements, réseaux, sources.',
    enqueter: 'Faut-il enquêter sur {nom} ?',
    enqueterSous: 'Proposez cette enquête au vote — c\'est la demande qui décide de ce que nous analysons ensuite.',
  },

  // La fiche acteur en trois colonnes (src/components/acteurs/FicheActeurTroisColonnes.tsx).
  enq3: {
    essentielSous: 'Ce que l’enquête établit à rebours de l’idée reçue. Choisir un fait : à droite, les proches qu’il nomme.',
    constatMajeur: 'Le constat le plus significatif',
    apparaitDansN: { one: 'revient dans {n} autre élément de l’enquête', other: 'revient dans {n} autres éléments de l’enquête' },
    nommeN: { one: 'nomme {n} proche', other: 'nomme {n} proches' },
    sertAussiN: { one: 'sert {n} élément de l’enquête', other: 'sert {n} éléments de l’enquête' },
    depuis: 'depuis {annee}',
    ouApparait: 'Où il revient dans l’enquête',
    nulleParAilleurs: 'L’enquête ne le nomme nulle part ailleurs.',
    prochesNommes: 'Les proches qu’il nomme',
    memeSource: 'La même source sert aussi à',
    ouSert: 'Ce que l’enquête en tire',
    sourceGenerale: 'Aucun élément précis ne cite cette adresse : elle documente l’enquête dans son ensemble.',
    groupes: {
      essentiel: 'L’essentiel', parcours: 'Le parcours', formation: 'Formation', parcoursPrive: 'Parcours privé',
      affaires: 'Les procédures', enquetesEnCours: 'Enquêtes en cours', recoupement: 'Le recoupement, affaire par affaire',
      flux: 'Les flux documentés', conflits: 'Conflits d’intérêts documentés', patrimoine: 'Patrimoine', transactions: 'Transactions marquantes',
      construction: 'La construction, période par période', trainDeVie: 'Train de vie et biens',
      premierCercle: 'Le premier cercle', politique: 'En politique', milieuAffaires: 'Dans les affaires', presse: 'Dans la presse', international: 'À l’international',
      cerclesInfluence: 'Cercles d’influence', liensInattendus: 'Liens inattendus', nominations: 'Comment il nomme', protection: 'Protection institutionnelle', opposants: 'Ses opposants',
      mecanismes: 'Les mécanismes', amplifie: 'Ce qui est amplifié', disparait: 'Ce qui disparaît', favorables: 'Médias décrits comme favorables', critiques: 'Médias décrits comme critiques',
      contradictions: 'Ce qui a été dit, ce qui a été fait', ordinaire: 'Ce qui s’écarte de l’ordinaire', schemas: 'Les schémas qui reviennent', hypotheses: 'Hypothèses concurrentes',
      centrale: 'La question centrale', questions: 'Les autres questions', rumeursEcartees: 'Rumeurs écartées faute de recoupement', manque: 'Ce qui manque pour trancher', biais: 'Les biais possibles de cette enquête',
      sources: 'Les sources',
    },
    etiquettes: {
      decisions: 'Ce que l’enquête relève', anomalies: 'Ce qui détonne', refutation: 'Éléments de réfutation',
      declare: 'Déclaré', estime: 'Estimé', ecart: 'Écart', benefices: 'Ce que chacun y gagne', roleReel: 'Rôle officiel, rôle réel',
      observe: 'Observé', attendu: 'Attendu', intention: 'Si c’est une intention', coincidence: 'Si c’est une coïncidence', systeme: 'Si c’est le système',
      raison: 'Pourquoi elle est écartée', biais: 'Ce qui oriente cette source',
    },
  },
  veille3: {
    sommaire: 'Sommaire de la veille',
    surtitre: 'Veille',
    titre: 'Quoi lire',
    fil: 'Tout le fil',
    groupeMoment: 'Par moment',
    groupeNature: 'Par nature',
    avenir: 'À venir',
    semaine: 'Cette semaine',
    mois: 'Ce mois-ci',
    ancien: 'Plus ancien',
    major_evolution: 'Évolutions majeures',
    contradiction: 'Contradictions',
    scenario_update: 'Perspectives',
    new_relationship: 'Nouvelles relations',
    sujets: 'Les sujets suivis',
    unite: 'dépêches',
    uniteSujets: 'sujets',
    critiquesSeules: 'Critiques seulement',
    nonDatees: 'Sans date d’événement',
    nonDateesAide: 'Des contradictions relevées par la veille sur des faits plus anciens : elles ne sont pas de l’actualité du jour.',
    releveeLe: 'relevée le {date}',
    contexte: 'Contexte',
    quiBouge: 'Ce qui bouge le plus',
    quiBougeAide: 'Les sujets aux dépêches les plus nombreuses sur trente jours.',
    nRecents: { one: '{n} dépêche en 30 jours', other: '{n} dépêches en 30 jours' },
    laSuite: 'La suite de ce sujet',
    seule: 'Aucune autre dépêche sur ce sujet.',
    voirFiche: 'Voir la fiche →',
    toutLeSujet: 'Tout le sujet au centre →',
    sujetChoisi: 'Le fil : {sujet}',
    retirerSujet: 'Revenir au fil complet',
    choisir: 'Choisir une dépêche affiche son résumé, ses sources et la suite de son sujet.',
    rien: 'Rien dans cette entrée pour ces filtres.',
    pivot: 'Changer de menu',
    parSujet: 'Par sujet',
    nSujets: { one: '{n} sujet', other: '{n} sujets' },
  },
  org3: {
    tous: 'Tous',
    legendeToile: 'Plus le lien est fort (force de 1 à 10), plus l’organisation est proche et le trait épais ; pointillé : force non renseignée',
    legendeMembres: 'Anneau intérieur : membres actuels · extérieur : anciens membres, invités, et membres sans période datée',
    essentielSous: 'Ce qu’il faut savoir de l’organisation. À droite : la fiche en bref, et les personnes qui y reviennent le plus.',
    enBref: 'En bref',
    fondee: 'Fondée',
    siege: 'Siège',
    statut: 'Statut',
    pays: { one: 'Active dans {n} pays', other: 'Active dans {n} pays' },
    quiRevient: 'Ceux qui reviennent le plus dans la fiche',
    nElements: { one: '{n} élément', other: '{n} éléments' },
    apparaitDansN: { one: 'revient dans {n} autre élément de la fiche', other: 'revient dans {n} autres éléments de la fiche' },
    nommeN: { one: 'nomme {n} membre de la fiche', other: 'nomme {n} membres de la fiche' },
    sertAussiN: { one: 'sert {n} élément de la fiche', other: 'sert {n} éléments de la fiche' },
    ouApparait: 'Où il revient dans la fiche',
    nulleParAilleurs: 'La fiche ne le nomme nulle part ailleurs.',
    nommes: 'Les membres qu’il nomme',
    memeSource: 'La même source sert aussi à',
    ouSert: 'Ce que la fiche en tire',
    sourceGenerale: 'Aucun élément précis ne cite cette adresse : elle documente la fiche dans son ensemble.',
    roles: {
      fondateur: 'Fondateur', membre: 'Membre', invitesStrategiques: 'Invité stratégique', opposantsCooptes: 'Opposant coopté',
      leadersEmergents: 'Leader émergent', multiAffilie: 'Membre partagé avec d’autres organisations',
    },
    groupes: {
      identite: 'Ce qu’elle dit être, ce qu’elle vise', phases: 'Les phases', fondation: 'La fondation', fondateurs: 'Les fondateurs', structure: 'La structure',
      invitesStrategiques: 'Invités stratégiques', opposantsCooptes: 'Opposants cooptés', leadersEmergents: 'Leaders émergents cultivés',
      recommandations: 'Les recommandations suivies', crises: 'Les crises vues venir', canaux: 'Les canaux d’influence', evenements: 'Les rendez-vous',
      financements: 'Les sources de financement', circuit: 'Le circuit de l’argent', conflits: 'Les conflits d’intérêts', depenses: 'Où va l’argent', manquements: 'Les manquements à la transparence',
      ecosysteme: 'L’écosystème', orgsLiees: 'Les organisations liées', multiAffilies: 'Les membres partagés', position: 'Sa position dans le réseau', coordination: 'Comment le réseau se coordonne',
      verdict: 'Le verdict', constats: 'Les constats clés', anomalies: 'Les anomalies', problematiques: 'Ce qui pose problème', legitimes: 'Les contributions légitimes',
      recommandationsFiche: 'Ce que la fiche recommande', effets: 'Ce que ça produit',
      limites: 'Les limites des données', appui: 'La solidité de la fiche', pistes: 'Les pistes d’enquête', sources: 'Les sources',
    },
    etiquettes: {
      realisations: 'Réalisations', niveau: 'Niveau d’influence', motivations: 'Motivations analysées', connexions: 'Connexions', trajectoire: 'Trajectoire',
      nonDivulgues: 'Sujets non divulgués', contributeurs: 'Principaux contributeurs', conditions: 'Conditions', etapes: 'Les étapes', roi: 'Ce que ça rapporte',
      preuves: 'Preuves', biais: 'Ce qui oriente cette source',
    },
  },
  bs3: {
    vue: 'Vue d’ensemble',
    vueTitre: 'Les promesses de {nom}, confrontées aux actes',
    vueChapo: 'Note de cohérence : {note}/10 — {libelle}. Chaque constat ouvre sa rubrique ; chaque promesse porte un numéro, le même partout sur la page.',
    groupeVerdict: 'Le verdict',
    retenir: 'Ce qu’il faut retenir',
    attention: 'Points d’attention',
    schema: 'Le schéma récurrent',
    note: 'Pourquoi cette note',
    groupePromesses: 'Les promesses',
    toutes: 'Toutes',
    declarations: 'Déclarations',
    groupeDepuis: 'Depuis',
    depuis: 'Ce qu’il a dit depuis',
    nonTenusSur: { one: '{n} non tenu sur {total}', other: '{n} non tenus sur {total}' },
    nTenus: { one: '{n} tenu', other: '{n} tenus' },
    nPartiels: { one: '{n} partiellement tenu', other: '{n} partiellement tenus' },
    nNonTenus: { one: '{n} non tenu', other: '{n} non tenus' },
    nDeclarations: { one: '{n} déclaration', other: '{n} déclarations' },
    nNonEvaluables: { one: '{n} non évaluable', other: '{n} non évaluables' },
    domaines: 'Par domaine',
    moinsTenu: 'Le moins tenu : {domaine}, {note}/10',
    mieuxTenu: 'Le mieux tenu : {domaine}, {note}/10',
    depuisReserve: 'Les prises de position relevées depuis, promesse par promesse',
    nPositions: { one: '{n} prise de position relevée depuis', other: '{n} prises de position relevées depuis' },
    nPositionsCourt: { one: '{n} position depuis', other: '{n} positions depuis' },
    depuisSous: { one: 'Rattachée à {n} promesse', other: 'Rattachées à {n} promesses' },
    depuisChapo: 'Ce que la veille a relevé de sa parole, rattaché à la promesse qu’il touche. Un trait plein : un lien ; des pointillés : un rapprochement.',
    depuisTitre: 'Ce qu’il a dit depuis',
    depuisReserveLong: 'Les prises de position relevées depuis, rattachées à chaque promesse, sont réservées aux abonnés.',
    enFrancais: 'Prises de position relevées en français.',
    rapprochement: 'rapprochement',
    rapprochementAide: 'Un rapprochement : la position touche le sujet de cette promesse sans s’y référer directement.',
    lienAide: 'La position porte directement sur cette promesse.',
    laPromesseTouchee: 'La promesse qu’elle touche',
    analyse: 'Dans l’analyse « {titre} »',
    parDomaine: 'Les promesses, domaine par domaine',
    parDomaineAide: 'Chaque pastille est une promesse, avec sa note de tenue sur 10 (en pointillés : une déclaration, notée sur sa franchise). Un clic l’ouvre.',
    commentTitre: 'Comment ce bilan est fait',
    selection: '{collectes} engagements publics collectés, {retenus} retenus pour ce bilan.',
    ecartes: 'Écartés de la sélection',
    lesPires: 'Les moins tenues',
    constatsAide: 'Choisir un constat : à droite, les promesses qu’il évoque, chacune avec son numéro.',
    promessesInvoquees: 'Les promesses qu’il évoque',
    promessesInvoqueesSous: 'Un clic ouvre la promesse en détail.',
    promessesNommeesSous: 'Reconnues aux termes du passage. Un clic ouvre la promesse en détail.',
    aucuneInvoquee: 'Ce passage ne renvoie à aucune promesse en particulier.',
    nPromesses: { one: '{n} promesse', other: '{n} promesses' },
    tropVague: 'trop vague pour être mesurée',
    nReserves: { one: '{n} réserve', other: '{n} réserves' },
    promesseN: 'Promesse {n}',
    ceQueDitLeVerdict: 'Ce qu’en dit le verdict',
    precedente: 'Précédente',
    suivante: 'Suivante',
  },
  plus3: {
    vue: 'Vue d’ensemble',
    vueChapo: 'Ce que l’analyse documente au-delà des faits : d’où vient l’affaire, où elle peut aller, qui elle touche, et sur quoi elle repose. Chaque constat ouvre sa rubrique.',
    groupeTemps: 'Le temps',
    groupeGens: 'Les gens',
    groupeMethode: 'La méthode',
    precedents: 'Précédents',
    impact: 'Qui est touché',
    reseaux: 'Les réseaux',
    sources: 'Les sources',
    chronoConstat: { one: '{n} événement, de {debut} à {fin}', other: '{n} événements, de {debut} à {fin}' },
    chronoChapo: 'Dans l’ordre des dates. Choisir un événement : à droite, ce qu’il a changé et ce qu’en disent ses sources.',
    peuConnus: { one: 'Dont {n} peu connu.', other: 'Dont {n} peu connus.' },
    precedentsConstat: { one: '{n} précédent historique', other: '{n} précédents historiques' },
    precedentsChapo: 'Ce qui s’est déjà produit d’approchant : ce qui se ressemble, ce qui diffère, et ce qu’on peut en tirer.',
    sourcesDuPrecedent: 'Les sources que cite ce précédent. Un repère du texte éclaire la sienne.',
    scenarioHaut: 'Le plus probable : {titre} — {p} %',
    scenariosConstat: { one: '{n} scénario estimé', other: '{n} scénarios estimés' },
    scenariosChapo: { one: '{n} scénario. Choisir un scénario : à droite, ce qui le déclencherait, ce qu’il entraînerait, et qui il concerne.', other: '{n} scénarios, du plus probable au moins probable. Ils ne s’excluent pas tous : leurs probabilités n’ont pas à faire 100. Choisir un scénario : à droite, ce qui le déclencherait, ce qu’il entraînerait, et qui il concerne.' },
    echeance: 'échéance {date}',
    declencheurs: 'Ce qui le déclencherait',
    implications: 'Ce qu’il entraînerait',
    acteursConcernes: 'Les acteurs qu’il concerne',
    probaBouge: 'Comment sa probabilité a bougé',
    acteursConstat: { one: '{n} acteur, dont {k} discrets', other: '{n} acteurs, dont {k} discrets' },
    plusGrandEcart: 'Le plus grand écart : {nom}, {n} points d’influence de plus que de visibilité',
    nuageAria: 'Les acteurs placés selon leur visibilité et leur influence',
    zoneDiscrete: 'Agit plus qu’il ne se voit',
    nuageAide: 'Au-dessus de la diagonale : plus d’influence que de visibilité. Un point se choisit.',
    famille: { visible: 'Visibles', discret: 'Discrets', victime: 'Victimes' },
    familleTitre: { visible: 'Les visibles, par influence', discret: 'Les discrets, par écart', victime: 'Les victimes' },
    tous: 'Tous',
    jaugesAide: 'Sur chaque ligne : l’influence (barre sombre), puis la visibilité (barre violette).',
    lectures: 'Ce que montre la distribution',
    lecturesChapo: 'Les lectures de l’analyse sur l’ensemble des acteurs. Choisir un acteur : à droite, sa position.',
    lectureEcart: 'Influence et visibilité',
    lectureVictimes: 'Ce que subissent les victimes',
    scenariosDeLActeur: 'Les scénarios où il compte',
    voirFiche: 'Voir sa fiche',
    profilsConstat: { one: '{n} profil touché', other: '{n} profils touchés' },
    plusTouche: 'Le plus touché : {profil}, {n}/10',
    ecartConstat: 'Écart de {n}/100 entre ce que disent les médias et ce qui se dit en ligne',
    recitsQuiMontent: 'Les récits qui montent',
    parPlateforme: 'Par plateforme',
    sourcesConstat: { one: '{n} source', other: '{n} sources dans {cats} familles' },
    sourcesChapo: 'De la plus citée à la moins citée. Pour chacune : sa fiabilité notée sur 10, son biais, et ce qu’elle porte dans l’analyse.',
    sourcesAide: 'Choisir une source : à droite, ce qu’elle est et où l’analyse la cite.',
    plusCitee: 'La plus citée : {media}, par {n} éléments de l’analyse',
    toutes: 'Toutes',
    fiabilite: 'Fiabilité notée sur 10',
    citeeParN: { one: 'citée par {n} élément', other: 'citée par {n} éléments' },
    citeeNullePart: 'citée par aucun élément détaillé',
    citeeNullePartLong: 'Aucun fait, récit, événement, précédent, scénario ni profil ne la cite par sa balise. Elle peut nourrir l’introduction ou le cadrage.',
    quiParle: 'Qui parle',
    passagesRetenus: 'Les passages retenus par l’analyse',
    ouCitee: 'Où l’analyse la cite',
    lieu: {
      fait: { one: '{n} fait établi', other: '{n} faits établis' },
      recit: { one: '{n} récit', other: '{n} récits' },
      chronologie: { one: '{n} événement', other: '{n} événements' },
      precedent: { one: '{n} précédent', other: '{n} précédents' },
      scenario: { one: '{n} scénario', other: '{n} scénarios' },
      impact: { one: '{n} profil touché', other: '{n} profils touchés' },
    },
    perspectivesManquantes: 'Les points de vue qui manquent',
    biais: 'Les biais signalés',
    acces: 'Ce qui n’a pas pu être consulté',
    aSuivre: 'À suivre',
    aSuivreChapo: 'Les échéances des scénarios, et les événements peu connus de la chronologie.',
    echeances: 'Les prochaines échéances',
    peuConnusTitre: 'Peu connus',
    rienASuivre: 'Aucune échéance à venir.',
    pourquoiPeuConnu: 'Pourquoi il est peu connu',
    portee: 'Ce que ça a changé',
    sansSource: 'Aucune source citée.',
    avant: 'Avant',
    apres: 'Après',
  },
  faits3: {
    rubriques: 'Rubriques de l’onglet',
    contexte: 'Contexte',
    vue: 'Vue d’ensemble',
    groupeEtabli: 'Ce qui est établi',
    groupeDivise: 'Ce qui divise',
    groupePlus: 'Voir aussi',
    faitsEtablis: 'Faits établis',
    fractures: 'Les fractures',
    debat: 'Ce que le débat ne dit pas',
    recits: 'Les récits',
    vueTitre: 'Ce qui est établi, ce qui divise',
    vueChapo: 'Les faits que les sources établissent, et ce que chaque récit en fait. Chaque constat ouvre la rubrique qui le détaille.',
    nFaits: { one: '{n} fait établi', other: '{n} faits établis' },
    niveau: { high: 'Certain', medium: 'Probable', low: 'Fragile' },
    niveauTitre: { high: 'Établis avec certitude', medium: 'Probables', low: 'Fragiles' },
    nNiveau: {
      high: { one: '{n} certain', other: '{n} certains' },
      medium: { one: '{n} probable', other: '{n} probables' },
      low: { one: '{n} fragile', other: '{n} fragiles' },
    },
    nContestes: { one: '{n} contesté par au moins une source', other: '{n} contestés par au moins une source' },
    socleTitre: 'Le socle commun',
    socleConstat: { one: '{n} fait dont tous les récits citent les sources', other: '{n} faits dont tous les récits citent les sources' },
    socleAucun: 'Aucun fait dont tous les récits citent les sources',
    socleChapo: 'Des lectures opposées citent les mêmes sources sur ces faits : le désaccord porte ailleurs.',
    propresTitre: 'Ce que chaque récit garde pour lui',
    propresChapo: 'Les faits dont un seul récit cite les sources.',
    seulRecit: 'Seul le récit {n} en cite les sources',
    matriceTitre: 'Quels récits citent les sources de quels faits',
    matriceAide: 'Un point : le récit cite au moins une source du fait ; plus il est gros, plus elles sont nombreuses. Citer une source n’est pas l’approuver.',
    recitN: 'Récit {n}',
    nSources: { one: '{n} source', other: '{n} sources' },
    ceQueDisent: 'Ce que disent ses sources',
    sansCitation: 'L’analyse n’a pas retenu de passage de cette source pour ce fait.',
    elements: 'Les éléments retenus',
    quiConteste: 'Qui le conteste',
    nonConteste: 'Aucune source ne le conteste.',
    recitsQuiCitent: 'Les récits qui citent ses sources',
    aucunRecit: 'Aucun récit ne cite ses sources.',
    lireRecit: 'Lire le récit',
    reperesAide: 'Choisir un repère numéroté : à droite, la source qu’il cite.',
    sourcesCitees: 'Les sources citées',
    faitsMemesSources: 'Faits établis sur les mêmes sources',
    aucunFaitMemesSources: 'Aucun fait établi ne repose sur ces sources.',
    faitsDuRecit: 'Faits établis dont il cite les sources',
    aucunFaitDuRecit: 'Il ne cite les sources d’aucun fait établi.',
    contredit: { one: '{n} élément le contredit', other: '{n} éléments le contredisent' },
    recitsTitre: { one: '{n} récit sur la même affaire', other: '{n} récits sur la même affaire' },
    recitsChapo: 'Choisir un récit : à droite, les faits établis dont il cite les sources.',
    faitsCites: { one: '{n} fait établi cité', other: '{n} faits établis cités' },
  },
  acteur3: {
    rubriques: 'Rubriques de la fiche',
    contexte: 'Contexte',
    chargement: 'Chargement…',
    groupePublie: 'Ce qui est établi',
    groupeReseau: 'Son réseau',
    groupeActu: 'Dans l’actualité',
    publications: 'Nos publications',
    reseau: 'Relations documentées',
    organisations: 'Appartenances',
    positions: 'Positions relevées',
    veille: 'Veille',
    publiTitre: { one: '{n} publication', other: '{n} publications' },
    publiChapo: 'Choisir une publication affiche, à droite, ce qui la relie au reste de la fiche.',
    consacrees: 'Consacrées à {nom}',
    analysesRole: 'Les analyses où {nom} tient un rôle',
    verifications: 'Les affirmations vérifiées',
    analyse: 'Analyse',
    verification: 'Vérification d’une affirmation',
    comptage: { one: '{n} engagement notable : {detail}', other: '{n} engagements notables : {detail}' },
    nTenus: { one: '{n} tenu', other: '{n} tenus' },
    nTrahis: { one: '{n} trahi', other: '{n} trahis' },
    nIntermediaires: { one: '{n} intermédiaire', other: '{n} intermédiaires' },
    role: 'Rôle : {role}',
    leConstat: 'Le constat principal',
    laQuestion: 'La question restée ouverte',
    lEssentiel: 'L’essentiel de l’analyse',
    leVerdict: 'Le verdict',
    reseauBarometre: 'Dans son réseau, aussi passés au baromètre',
    aussiDansAnalyse: 'Aussi dans cette analyse, et dans son réseau',
    aucunCroisement: 'Aucune personne de son réseau documenté n’y tient de rôle.',
    ouvrirBarometre: 'Ouvrir le baromètre',
    lireInvestigation: 'Lire l’investigation',
    ouvrirAnalyse: 'Ouvrir l’analyse',
    lireVerification: 'Lire la vérification',
    ouvrirFiche: 'Ouvrir la fiche de {nom}',
    ouvrirOrg: 'Ouvrir la fiche de l’organisation',
    citeesTitre: 'Citées dans ses relations, non établies',
    citeesAide: 'Des organisations que son réseau mentionne, sans qu’aucune liste de membres ou de participants ne l’y inscrive. Une mention n’est pas une appartenance : chacune est donnée avec la relation qui la porte et sa source.',
    citeesNonEtablie: { one: 'non établie · citée par {n} relation', other: 'non établie · citée par {n} relations' },
    citeesFait: 'fait rapporté',
    citeesLecture: 'notre lecture',
    citeesSource: 'source ↗',
    reseauTitre: { one: '{n} personne', other: '{n} personnes' },
    reseauChapo: 'Choisir une personne affiche, à droite, ce qui fonde la relation et où les deux fiches se croisent.',
    depuis: 'depuis {annee}',
    ceQuiFonde: 'Ce qui fonde la relation',
    lecture: 'lecture, pas un fait établi',
    dossierRetient: 'Le dossier {deNom} retient : « {intitule} »',
    etabliPar: 'Établi par {qui}',
    sansSource: 'aucune source citée pour ce lien',
    seCroisent: 'Où les deux fiches se croisent',
    membresTousDeux: 'Organisation · membres tous les deux',
    analyseCommune: 'Analyse · un rôle pour chacun',
    aSonBarometre: 'A son propre baromètre',
    rienEnCommun: 'Aucun autre croisement sur le site que cette relation.',
    orgsTitre: { one: '{n} organisation', other: '{n} organisations' },
    orgsChapo: 'Choisir une organisation affiche, à droite, ses autres membres qui figurent aussi dans son réseau, et les rencontres où sa présence est relevée.',
    membresRepertories: { one: '{n} autre membre répertorié', other: '{n} autres membres répertoriés' },
    dontReseau: { one: 'dont {n} dans son réseau', other: 'dont {n} dans son réseau' },
    poste: 'Poste relevé',
    membresDuReseau: 'Aussi membres, et dans son réseau',
    aucunMembreReseau: 'Aucun autre membre répertorié ne figure dans ses relations documentées.',
    rencontres: 'Rencontres où sa présence est relevée',
    orgNonPubliee: 'La fiche de cette organisation n’est pas encore publiée.',
    positionsTitre: { one: '{n} position relevée', other: '{n} positions relevées' },
    positionsChapo: 'Ce que nos analyses et notre veille ont relevé, daté et sourcé. Choisir une position affiche sa source.',
    laPosition: 'La position',
    enFrancais: 'Texte en français : les positions relevées ne sont pas encore traduites.',
    source: 'Source',
    sansSourcePosition: 'Aucune adresse de source pour cette position.',
    analyseLiee: 'Relevée dans l’analyse',
    veilleTitre: 'Ce que la veille a relevé',
    // Maquette E2 (16/10/2026)
    vue: 'Vue d’ensemble',
    vueTitre: 'Ce que la fiche établit',
    vueChapo: 'Les constats tirés de nos publications et de nos relevés. Chacun ouvre la rubrique qui le détaille.',
    promesses: 'Promesses',
    investigation: 'Investigation',
    analysesVerifs: 'Analyses et vérifications',
    fil: 'Fil de l’actualité',
    barometreDate: 'Baromètre des promesses · {date}',
    investigationDate: 'Investigation · {date}',
    reseauConstat: { one: '{n} personne documentée', other: '{n} personnes documentées' },
    dontCercles: { one: ', dont {n} dans les cercles de l’investigation', other: ', dont {n} dans les cercles de l’investigation' },
    orgConstat: '{org} : {k} des {m} autres membres sont dans son réseau',
    filConstat: 'Dernière le {date} : {titre}',
    anaRole: { one: 'Un rôle dans {n} analyse', other: 'Un rôle dans {n} analyses' },
    nVerifs: { one: '{n} affirmation vérifiée', other: '{n} affirmations vérifiées' },
    nRattachees: { one: '{n} position du fil rattachée', other: '{n} positions du fil rattachées' },
    partoutTitre: 'Les personnes qui reviennent partout',
    partoutChapo: 'Croisement des relations documentées, des cercles de l’investigation, des organisations, des analyses et des baromètres.',
    partoutVide: 'Personne dans son réseau ne recroise la fiche ailleurs que par sa relation.',
    nCroisements: { one: '{n} croisement', other: '{n} croisements' },
    badgeRelation: 'Relation',
    badgeInvestigation: 'Investigation',
    badgeAnalyse: 'Analyse',
    badgeBarometre: 'Son baromètre',
    laParole: 'La parole',
    conclusion: 'Ce que le baromètre en conclut',
    depuisBarometre: 'Ce que le fil de l’actualité y rattache',
    aucunePositionPromesse: 'Aucune position du fil ne se rattache à cet engagement.',
    rapprochement: 'Rapprochement',
    rapprochementAide: 'Lien proposé par un passage automatique, avec une confiance moyenne : à lire comme une piste.',
    chiffrable: 'chiffrable',
    qualitatif: 'qualitatif',
    autresAngles: 'Autres angles du baromètre',
    lesCercles: { one: 'Les cercles établis par l’investigation · {n} personne ou entité', other: 'Les cercles établis par l’investigation · {n} personnes et entités' },
    cerclesPremium: { one: 'La personne ou l’entité de ses cercles est réservée aux abonnés.', other: 'Les {n} personnes et entités de ses cercles sont réservées aux abonnés.' },
    dansInvestigation: 'Dans l’investigation · {cercle}',
    relationDocumentee: 'Relation documentée',
    investigationSur: 'Investigation sur {nom}',
    cercleNomme: 'Cercle « {cercle} »',
    sansFiche: 'Pas encore de fiche sur le site.',
    tous: 'Tous',
    toutes: 'Toutes',
    legendeAnneaux: 'Anneau intérieur : cercle proche · milieu : politique · extérieur : affaires, médias, international, inattendus',
    reseauKicker: 'Réseau · relations documentées et cercles de l’investigation',
    reseauTitreE2: { one: '{n} personne ou entité', other: '{n} personnes et entités' },
    orgsTitreE2: 'Où son réseau se retrouve',
    orgsChapoE2: 'Choisir une organisation éclaire, dans le graphe, ceux de ses membres qui figurent dans son réseau.',
    membreDepuis: 'Membre · {periode}',
    enMemeTemps: 'Y siégeaient en même temps',
    enMemeTempsAide: 'Membres dont la période d’appartenance, datée, recoupe la sienne. Siéger dans la même organisation n’est pas une relation documentée.',
    legendeEnMemeTemps: 'pointillé : y siégeaient en même temps, sans relation documentée',
    depuisAn: 'depuis {an}',
    enAn: 'en {an}',
    aussiReseau: 'aussi dans son réseau',
    duReseau: '{n} du réseau',
    filTitreE2: 'De {debut} à {fin}',
    nature: {
      contradiction: 'Contradiction',
      media: 'Apparition',
      role_change: 'Nomination',
    },
    nNatures: {
      contradiction: { one: '{n} contradiction', other: '{n} contradictions' },
      media: { one: '{n} apparition médiatique', other: '{n} apparitions médiatiques' },
      role_change: { one: '{n} nomination', other: '{n} nominations' },
    },
    rattacheAnalyse: 'Analyse dont la position relève',
    rattachePromesse: 'Engagement du baromètre qu’elle touche',
    positionsRattachees: 'Positions du fil rattachées à cette analyse',
    analyseDeLaVerif: 'Analyse dont elle relève',
    analysesFil: 'Les analyses où son fil d’actualité le mène',
    entree: 'Qui l’y a fait entrer',
    trouveDans: 'Trouvé dans : {ou}',
    ouRelation: 'une relation documentée',
    ouInvestigation: 'les cercles de l’investigation',
  },

  partages: {
    voirMoins: 'Voir moins',
    premiumReserve: 'Contenu réservé aux abonnés Premium',
    premiumSansAcces: 'Votre compte actuel ne donne pas accès à ce contenu. Un abonnement Premium est requis.',
    premiumConnexion: 'Ce contenu est réservé aux abonnés Premium. Connectez-vous si vous êtes déjà abonné.',
    seConnecter: 'Se connecter',
    devenirPremium: 'Devenir Premium →',
    retirerPortfolio: 'Retirer du portfolio',
    sauverPortfolio: 'Sauvegarder dans mon portfolio',
    enregistre: 'Enregistré',
    sauvegarder: 'Sauvegarder',
    reseauApercu: 'Réseau — aperçu',
    apercuDe: 'Aperçu du réseau de {nom}',
    reseauDocumente: 'Réseau documenté',
    ouvrirGraphe: 'Ouvrir dans Le Graphe →',
    historique: 'Historique de la fiche',
    historiqueTexte: 'Cette fiche est mise à jour en continu par notre veille. Chaque modification est journalisée ici, sources à l\'appui.',
    voirEntrees: { one: 'Voir {n} entrée de plus', other: 'Voir {n} entrées de plus' },
    signalRecent: 'Signal récent',
    exploite: 'exploité',
    confirme: 'confirmé',
    nouvellesRecentes: 'Nouvelles récentes ({n})',
    voirNouvelles: { one: 'Voir {n} nouvelle de plus', other: 'Voir {n} nouvelles de plus' },
    contradictions: 'Contradictions détectées ({n})',
    voirContradictions: { one: 'Voir {n} contradiction de plus', other: 'Voir {n} contradictions de plus' },
  },

  proposer: {
    rayonx: {
      kicker: 'Une affirmation à vérifier ?',
      sub: 'Soumettez-la — la communauté vote, on la passe au crible.',
      formTitle: 'Soumettre une affirmation au RayonX',
      titlePlaceholder: 'L\'affirmation à vérifier',
      descPlaceholder: 'Contexte / source (optionnel)',
    },
    geo: {
      kicker: 'Un sujet à décrypter ?',
      sub: 'Proposez-le — la communauté vote, on l\'analyse sous plusieurs angles.',
      formTitle: 'Proposer une analyse',
      titlePlaceholder: 'Le sujet à analyser',
      descPlaceholder: 'Pourquoi ce sujet est-il pertinent ? (optionnel)',
    },
    actor: {
      kicker: 'Un acteur à passer au crible ?',
      sub: 'Proposez-le — la communauté vote, on étudie sa cohérence et ses réseaux.',
      formTitle: 'Proposer un acteur',
      titlePlaceholder: 'Nom de l\'acteur',
      descPlaceholder: 'Pourquoi cet acteur ? (optionnel)',
    },
    org: {
      kicker: 'Une organisation à cartographier ?',
      sub: 'Proposez-la — la communauté vote, on cartographie ses réseaux d\'influence.',
      formTitle: 'Proposer une organisation',
      titlePlaceholder: 'Nom de l\'organisation',
      descPlaceholder: 'Pourquoi cette organisation ? (optionnel)',
    },
    echecEnvoi: 'La proposition n\'a pas pu être envoyée.',
    erreurReseau: 'Erreur réseau, réessayez.',
    erreurN: 'Erreur {n}',
    envoyee: 'Proposition envoyée — elle rejoint l\'Agora, soumise au vote.',
    lancee: 'Analyse lancée.',
    voirResultat: 'Voir le résultat →',
    voirAgora: 'Voir l\'Agora →',
    autre: 'Proposer un autre',
    proposer: 'Proposer →',
    connectez: 'Connectez-vous pour proposer →',
    region: 'Région',
    domaine: 'Domaine',
    annuler: 'Annuler',
    envoi: 'Envoi…',
    soumettre: 'Soumettre la proposition',
    lancement: 'Lancement…',
    executer: 'Exécuter maintenant',
  },

  orientation: {
    releves: {
      media: 'Apparition médiatique',
      contradiction: 'Écart relevé',
      role_change: 'Changement de fonction',
      autre: 'Relevé',
    },
    appartenances: {
      core_member: 'membre',
      alumni: 'ancien membre',
      invitee: 'invité',
      observer: 'observateur',
      board_member: 'membre du conseil',
    },
    liens: {
      inner_circle: 'cercle rapproché',
      political: 'lien politique',
      corporate: 'lien économique',
      media: 'lien médiatique',
      family: 'lien familial',
      financial: 'lien financier',
      international: 'lien international',
      institutional: 'lien institutionnel',
      autre: 'lien',
    },
    laFiche: 'la fiche {deNom}',
    uneFicheOrg: 'une fiche d’organisation',
    lienAvec: '{type} avec {sujet}',
    dossierRetient: ', dont le dossier retient « {libelle} »',
    analyseReseau: 'notre analyse du réseau {deNom}',
    lienNonQualifie: 'lien non qualifié',
    depuis: ' (depuis {date})',
    investigationSur: 'l\'investigation sur {sujet}',
    unAutreActeur: 'un autre acteur',
    cercle: ', cercle « {cercle} »',
    evenement: 'événement',
    notreAnalyse: 'notre analyse « {titre} »',
    uneDeNosAnalyses: 'une de nos analyses',
    analyseOuNom: 'notre analyse, où le nom apparaît',
    revientDans: ' — le nom revient dans {n} de ses sous-enquêtes',
    anatomieOuNom: 'notre anatomie, où le nom figure au relevé',
    limiteRienEcrit: 'Rien ici n\'a été écrit sur cette personne : chaque ligne a été produite en analysant quelqu\'un ou quelque chose d\'autre.',
    limitePointDeVue: { one: 'Le point de vue est celui du dossier {sujets} — c\'est là que ces liens ont été relevés, et ils décrivent ce que ces dossiers en retiennent.', other: 'Le point de vue est celui des dossiers {sujets} — c\'est là que ces liens ont été relevés, et ils décrivent ce que ces dossiers en retiennent.' },
    limiteSansAdresse: '{n} élément(s) sur {total} ne portent aucune adresse vérifiable : ils sont signalés comme tels.',
    limiteAbsence: 'L\'absence d\'un fait ici ne veut pas dire qu\'il n\'existe pas : elle veut dire que nous ne l\'avons pas établi.',
    cadrage: 'Nous n\'avons pas d\'analyse consacrée à {nom}. Cette page réunit ce que nos autres travaux ont croisé à son sujet, avec chaque fois le dossier qui l\'établit et l\'adresse où le vérifier. Ce n\'est pas un portrait, et l\'addition de ces lignes ne vaut pas conclusion.',
    fiche: {
      etabliPar: 'établi par',
      lecture: '· lecture, non établie comme un fait',
      aucuneAdresse: 'aucune adresse vérifiable',
      reperes: 'Repères — pas une analyse {deNom}',
      fonction: 'Fonction relevée dans nos données :',
      releves: 'Relevés dans nos analyses',
      relevesSous: 'Consignés et datés au fil de nos travaux, chacun avec sa nature.',
      appartenances: 'Appartenances',
      appartenancesSous: 'Relevées sur les fiches des organisations concernées.',
      liens: 'Liens relevés',
      liensSous: 'Décrits du point de vue du dossier qui les a établis, jamais du sien.',
      cercles: 'Cercles d\'investigation',
      cerclesSous: 'Position dans l\'entourage d\'une personne que nous avons enquêtée.',
      evenements: 'Événements',
      evenementsSous: 'Présences attestées sur les fiches d\'organisation.',
      mentions: 'Mentionné dans nos travaux',
      mentionsSous: 'Le nom y apparaît — à lire pour le contexte, pas comme une conclusion.',
      assemble: 'Assemblé le {date} à partir de {natures} type(s) de matière, dont {sourcees} élément(s) portant une adresse vérifiable.',
      rienPublie: 'Nous n\'avons rien publié sur cette personne',
      rienPublieTexte: '{nom} figure dans nos données parce qu\'un de nos travaux a cité ce nom. Nous n\'avons produit aucune analyse, aucun lien documenté ni aucune source à son sujet.',
      absence: 'Cette absence ne dit rien de la personne : elle dit ce que nous n\'avons pas fait.',
    },
  },

  acteurs: {
    tous: 'Tous',
    kicker: 'Personnalités',
    kickerAnalysees: 'Personnalités · {n} analysées',
    titre: 'Les visages du pouvoir',
    barometresPublies: { one: 'baromètre publié', other: 'baromètres publiés' },
    notesGlobales: 'Les {n} notes globales des baromètres publiés',
    recherche: 'Nom, pays, rôle…',
    type: 'Type',
    analyse: 'Analyse',
    toutes: 'Toutes',
    barometre: 'Baromètre',
    investigation: 'Investigation',
    prochesDAbord: 'Votre région d’abord',
    plusContradictoires: 'Plus contradictoires d’abord',
    plusCoherents: 'Plus cohérents d’abord',
    nomAZ: 'Nom, de A à Z',
    nbPersonnalites: { one: '{n} personnalité', other: '{n} personnalités' },
    aucunCorrespond: 'Aucun acteur ne correspond',
    filtresRestrictifs: 'Vos filtres sont peut-être trop restrictifs. Élargissez la recherche ou réinitialisez-les.',
    reinitialiser: 'Réinitialiser les filtres',
    rienPourLinstant: 'Rien à afficher pour l\'instant',
    bientot: 'Les premiers profils d\'acteurs arrivent bientôt.',
    noteSur10: 'note du baromètre sur 10',
    metaTitre: 'Acteurs',
    metaDescription: 'Personnalités politiques, diplomates, lobbyistes — profils complets avec score de cohérence et réseau d\'influence.',
    metaIntrouvable: 'Acteur introuvable',
    metaPersonnalite: 'Personnalité',
    metaProfil: '{type} — Profil, score de cohérence et réseau d\'influence analysés par Lumière Noire.',
  },

  filtres: {
    effacerRecherche: 'Effacer la recherche',
    tri: 'Tri',
    filtresEtTri: 'Filtres et tri',
    retirer: 'retirer',
    toutEffacer: 'Tout effacer',
  },

  divers: {
    reserveAbonnes: 'Réservé aux abonnés.',
    seConnecter: 'Se connecter',
    sAbonner: 'S\'abonner →',
    voirWikimedia: 'Voir le fichier et sa licence sur Wikimedia',
    nonLisible: 'Organisation citée dans nos analyses — sa fiche n\'a pas encore été analysée.',
    sommaire: 'Sommaire',
    chargement: 'Chargement…',
    derniereMaj: 'Dernière mise à jour : {date}.',
    aCompleter: 'À COMPLÉTER',
  },

  rayonx: {
    domaines: {
      politique: 'Politique',
      'économie': 'Économie',
      'santé': 'Santé',
      justice: 'Justice',
      environnement: 'Environnement',
      science: 'Science',
      'société': 'Société',
      international: 'International',
      autre: 'Autre',
    },
    tri: {
      date_desc: 'Plus récents',
      date_asc: 'Plus anciens',
      score_desc: 'Mieux notés',
      score_asc: 'Moins bien notés',
    },
    statuts: {
      processing: 'En cours de vérification',
      failed: 'Échec de la vérification',
      draft: 'Privé',
      pending_review: 'En relecture',
      published: 'Publié',
      rejected: 'Refusé',
    },
    actions: {
      demander: {
        bouton: 'Demander la publication',
        explication: 'Votre RayonX part en relecture. Il reste privé jusqu\'à la décision de l\'équipe : publié, il sera visible de tous et pourra apparaître sur l\'accueil ; refusé, vous saurez pourquoi.',
        confirmer: 'Envoyer en relecture',
      },
      annuler: {
        bouton: 'Annuler la demande',
        explication: 'La demande de relecture est retirée. Votre RayonX reste privé.',
        confirmer: 'Annuler la demande',
      },
      retirer: {
        bouton: 'Repasser en privé',
        explication: 'Votre RayonX quitte le site public. Pour le republier, il faudra repasser par la relecture.',
        confirmer: 'Repasser en privé',
      },
    },
    actionEchouee: 'L\'action n\'a pas abouti.',
    erreurReseau: 'Erreur réseau.',
    visibleTous: 'Visible de tous',
    visibleVous: 'Visible par vous seul',
    motifRefus: 'Motif du refus :',
    neRienFaire: 'Ne rien faire',
    mesRayonx: 'Mes Rayons X',
    rayonxPublics: 'Rayons X publics',
    rayonx: 'Rayons X',
    rechercheChamp: 'Une affirmation, une personne…',
    domaine: 'Domaine',
    tous: 'Tous',
    compte: '{n} RayonX',
    pasEncore: 'Vous n\'avez pas encore lancé de révélateur.',
    aucunFiltre: 'Aucun révélateur pour ce filtre.',
    utilisezAgora: 'Utilisez le bouton « Exécuter maintenant » dans l\'Agora pour vérifier une affirmation.',
    aucunPublic: 'Aucun révélateur public pour l\'instant.',
    onglets: {
      verdict: 'Verdict',
      preuves: 'Preuves',
      limites: 'Limites',
      methode: 'Méthode',
    },
    methodePerimetre: 'Méthode & périmètre',
    formeAffirmative: 'Forme affirmative vérifiée',
    reformulee: 'Affirmation reformulée (neutre)',
    notesMethodo: 'Notes méthodologiques',
    perimetreAnalyse: 'Périmètre analysé',
    periode: 'Période',
    perimetreGeo: 'Périmètre géographique',
    termesCles: 'Termes clés',
    recherches: 'Recherches effectuées ({n})',
    recherchesPlus: { one: '+ {n} recherche supplémentaire →', other: '+ {n} recherches supplémentaires →' },
    verification: 'Vérification',
    nbPreuves: { one: '{libelle} · {n} preuve', other: '{libelle} · {n} preuves' },
    verdictComplet: 'Le verdict complet ↓',
    preuvesUneParUne: { one: 'La preuve, en détail ↓', other: 'Les {n} preuves, une par une ↓' },
    lienCopie: '✓ Lien copié',
    partager: 'Partager',
    pointsCles: 'Points clés',
    preuvesCollectees: 'Preuves collectées',
    aucunePreuve: 'Aucune preuve indexée',
    limitesEntites: 'Limites & entités',
  },

  preuves: {
    polarite: {
      confirms: 'Confirme',
      refutes: 'Réfute',
      nuance: 'Nuance',
    },
    poids: {
      eleve: 'élevé',
      modere: 'modéré',
      faible: 'faible',
    },
    types: {
      official: 'Officiel',
      media: 'Presse',
      academic: 'Académique',
      factchecker: 'Fact-checker',
      legal: 'Juridique',
      other: 'Autre',
    },
    filtres: {
      all: 'Toutes',
      confirms: 'Confirment',
      refutes: 'Réfutent',
      nuance: 'Nuancent',
    },
    preuveN: 'Preuve {n}',
    preuveNMin: 'preuve {n}',
    poidsLibelle: 'Poids {poids}',
    aucuneCategorie: 'Aucune preuve dans cette catégorie',
    allerA: 'Aller à la preuve {n}',
    voirPreuveN: 'Voir la preuve {n}',
    voirDansPreuves: 'Voir dans les preuves →',
    uneCase: 'Une case par preuve. Cliquez une couleur pour ne garder que ces preuves.',
  },

  verdictRx: {
    confiance: {
      high: 'Confiance élevée',
      medium: 'Confiance moyenne',
      low: 'Confiance faible',
    },
    questionPosee: 'Question posée',
    affirmationVerifiee: 'Affirmation vérifiée',
    contexte: 'Contexte : {texte}',
    verifieLe: '· Vérifié le {date}',
    raisonnement: 'Raisonnement détaillé',
    parSousAffirmation: 'Verdict par sous-affirmation',
    pourraitChanger: 'Le verdict pourrait changer si…',
    nuances: 'Nuances à garder en tête',
    entites: 'Entités impliquées',
    acteurs: 'Acteurs',
    organisations: 'Organisations',
    aucuneLimite: 'Aucune limite ou entité identifiée',
    roles: {
      subject: 'Sujet',
      witness: 'Témoin',
      source: 'Source',
    },
    exact: 'Ce qui est exact',
    inexact: 'Ce qui est inexact ou absent',
    preuvesUneParUne: { one: 'La preuve, en détail →', other: 'Les {n} preuves, une par une →' },
  },
  rx3: {
    sommaire: 'Sommaire du RayonX',
    entete: 'Le verdict et ses preuves',
    verdict: 'Le verdict',
    sous: 'Les sous-affirmations',
    preuves: 'Les preuves',
    methode: 'Sur quoi ça repose',
    sousSous: 'L’affirmation découpée en parties vérifiables, chacune avec son verdict.',
    preuvesSous: { one: '{n} preuve, rangée par sens. Choisir une preuve : à droite, sa source et ce qu’elle appuie.', other: '{n} preuves, rangées par sens. Choisir une preuve : à droite, sa source et ce qu’elle appuie.' },
    methodeSous: 'Comment l’affirmation a été comprise, qui la vérification mobilise, et où l’on a cherché.',
    reserves: 'Les réserves',
    toutes: 'Toutes les preuves',
    appuie: 'Ce qu’elle appuie dans le verdict',
    citeeParSous: 'Citée par la sous-affirmation',
    ouvrirSource: 'Ouvrir la source',
  },

  rayonxListe: {
    kicker: 'RayonX · vérification, preuves à l\'appui',
    titre: 'Vrai, faux, ou plus compliqué ?',
    legende: { one: 'affirmation passée aux rayons X', other: 'affirmations passées aux rayons X' },
    metaDefaut: 'Analyse et vérification des sources par Lumière Noire.',
  },

  analyse: {
    fermerMaj: 'Fermer cette mise à jour',
    detecteAuto: '{date} — détecté automatiquement',
    majMajeures: { one: 'Mise à jour majeure', other: 'Mises à jour majeures' },
    reduire: 'Réduire',
    voirLes: 'Voir les {n}',
    autresMaj: { one: '+ {n} autre mise à jour', other: '+ {n} autres mises à jour' },
    chargement: 'Chargement…',
    impossible: 'Impossible de charger cette analyse.',
    sommaire: {
      chronologie: 'Événements de la chronologie',
      historique: 'Précédents historiques',
      scenarios: 'Perspectives à venir',
      impact: 'Profils d’impact citoyen',
      resonance: 'Ressentis dominants en ligne',
      sources: 'Sources documentées',
    },
    analyses: 'Analyses',
    analyse: 'Analyse',
    consensusLectures: 'de consensus · {n} lectures concurrentes',
    version: 'Version {v} · {date}',
    versionLien: 'Version {v} →',
    pied: 'Lumière Noire · Analyse géopolitique multi-perspectives · {date}',
    onglets: {
      passe: 'Les faits',
      lectures: 'Perspectives',
      plus: 'Aller plus loin',
    },
    sectionsAria: 'Sections de l\'analyse',
    essentiel: {
      aria: 'L\'essentiel de l\'analyse',
      titre: 'L\'essentiel',
      uneMinute: '1 min de lecture',
      etabli: 'Ce qui est établi',
      divise: 'Ce qui divise',
      probable: 'Perspective la plus probable',
      suivi: '{initiale} % à l’analyse · revu le {date}',
      suiviJamais: '{initiale} % à l’analyse',
      lireComplete: 'Lire l\'analyse complète ↓',
      confiance: {
        high: 'Confiance haute',
        medium: 'Confiance moyenne',
        low: 'Confiance basse',
      },
    },
    fraicheur: {
      nonVerifie: 'Non vérifié',
      aJour: 'À jour',
      recommandee: 'Mise à jour recommandée',
      aVerifier: 'Données à vérifier',
      couverture: 'Couverture : {date}',
      verifieLe: 'Vérifié le {date}',
    },
    rendezVous: {
      aria: 'Les prochains rendez-vous des analyses',
      titre: 'Les prochains rendez-vous',
      sous: 'le scénario daté le plus proche de chaque sujet',
      probabilite: 'Probabilité estimée par l\'analyse',
    },
    liste: {
      kicker: 'Analyses · scénarios datés',
      titre: 'Lectures croisées',
      sujetsSuivis: { one: 'sujet suivi', other: 'sujets suivis' },
      recherche: 'Rechercher une analyse…',
      region: 'Région',
      domaine: 'Domaine',
      statut: 'Statut',
      toutes: 'Toutes',
      tous: 'Tous',
      publies: 'Publiés',
      brouillons: 'Brouillons',
      plusRecentes: 'Plus récentes',
      plusConsensuelles: 'Plus consensuelles',
      plusDisputees: 'Plus disputées',
      aucune: 'Aucune analyse ne correspond',
      bientot: 'Les premières analyses géopolitiques arrivent bientôt.',
      deConsensus: 'de consensus',
      metaDescription: 'Analyses géopolitiques approfondies — conflits, trajectoires de pouvoir, crises. Décryptage des faits, pas des opinions.',
      introuvable: 'Analyse introuvable',
    },
  },

  plusLoin: {
    chronologie: 'Chronologie',
    chronologieSub: '{n} événements · {caches} peu connus',
    historique: 'Profondeur historique',
    historiqueSub: '{n} précédents · Comparaisons « Alors / Aujourd\'hui »',
    impact: 'Impact citoyen',
    impactSub: '{n} profils concrets',
    resonance: 'Ce qu’en disent les réseaux',
    resonanceSub: '{n} ressentis dominants',
    resonanceEcart: ' · écart avec les médias {ecart}/100',
    acteurs: 'Acteurs',
    acteursSub: 'Visibles · Cachés · Victimes · Bénéficiaires',
    scenarios: 'Et maintenant ?',
    scenariosSub: '{n} scénarios possibles · probabilités · déclencheurs',
    sources: 'Sources complètes',
    sourcesSub: '{n} sources · {cats} catégories · scores /10',
    evolution: 'Évolution',
    retour: '← Aller plus loin',
    titre: 'Aller plus loin',
    premiumEvolution: 'Analyse des évolutions réservée aux abonnés Premium',
    premiumActeurs: 'Analyse des acteurs réservée aux abonnés Premium',
    premiumScenarios: 'Scénarios réservés aux abonnés Premium',
    premiumImpact: 'Impact citoyen réservé aux abonnés Premium',
  },

  lectures: {
    rechercheRien: 'Ce qui a été cherché derrière ce récit : aucun réseau documenté',
    rechercheN: { one: 'Ce qui a été cherché derrière ce récit : {n} réseau documenté', other: 'Ce qui a été cherché derrière ce récit : {n} réseaux documentés' },
    biaisFaible: 'Biais faible',
    biaisEleve: 'Biais élevé',
    biaisMoyen: 'Biais moyen',
    nbSources: '{n} sources',
    ceQueDit: 'Ce que dit cette lecture',
    detail: 'Le détail, argument par argument',
    argumentN: 'Argument {n}',
    preuvesAppui: '{n} preuves à l\'appui',
    contradictoires: '{n} éléments contradictoires',
    anglesMorts: '{n} angles morts',
    contexte: {
      recits: 'Les récits',
      these: 'La thèse',
      aide: 'Choisir un argument : à droite, ce qui le porte.',
      titre: 'Contexte',
      source: 'Source {n}',
      retrouve: 'Retrouvé dans la page',
      pourPhrase: 'pour la phrase « {debut} »',
      retenu: 'Passage retenu par l’analyse',
      pasRetrouve: 'La machine n’a pas retrouvé cette phrase dans la page.',
      illisible: 'Page illisible par la machine.',
      chiffresOk: 'Chiffres retrouvés : {liste}',
      chiffresManquants: 'Absents de ce passage : {liste}',
      enLangue: 'en {langue}',
      donnees: 'Les données de l’argument',
      cliquer: 'Un repère du texte montre le passage de sa phrase.',
      porte: 'Ce qui le porte',
      sansSource: 'Aucune source citée pour cet argument.',
      sansPassage: 'Aucun passage retenu de cette source.',
      ouvrir: 'Ouvrir la source',
      faits: 'Faits établis sur les mêmes sources',
      sansFait: 'Aucun fait établi de l’analyse ne repose sur ces sources.',
      nonConteste: 'Non contesté',
      contestePar: { one: 'Contesté par {n} source', other: 'Contesté par {n} sources' },
      toutLeRecit: 'Pour tout le récit',
    },
    relayeePar: 'Relayée par',
    construite: 'Comment cette lecture est construite',
    perspectives: 'Les perspectives',
    premium: 'Perspectives supplémentaires réservées aux abonnés Premium',
    choisir: 'Choisir une perspective',
    precedent: 'Précédent',
    suivant: 'Suivant',
  },

  typo: {
    ouvrant: '« ',
    fermant: ' »',
  },

  evolution: {
    importance: {
      major: 'Majeur',
      moderate: 'Modéré',
      minor: 'Mineur',
    },
    issues: {
      confirmed: 'Confirmé',
      partially_confirmed: 'Partiellement confirmé',
      invalidated: 'Invalidé',
      still_pending: 'En attente',
    },
    synthese: '◆ Synthèse de l\'évolution',
    perspectives: '⇄ Évolution des perspectives',
    aucunFait: 'Aucun fait modifié documenté.',
    avant: 'Avant',
    maintenant: 'Maintenant',
    aucunActeur: 'Aucun mouvement d\'acteur documenté.',
    aucuneDonnee: 'Aucune donnée de perspectives disponible.',
    bilan: '◈ Bilan des perspectives',
    bilanV: '◈ Bilan des perspectives de la v{v}',
    depuis: '◈ Depuis la v{v} : les perspectives suivies',
    depuisRien: 'Pas encore revue depuis la publication.',
    etape: '{date} : {avant} % → {apres} %',
    inattendus: 'Développements inattendus',
    onglets: {
      synthese: 'Synthèse',
      faits: 'Faits',
      acteurs: 'Acteurs',
      scenarios: 'Scénarios',
    },
  },

  faits: {
    certitude: 'Ce qu\'on sait avec certitude',
    contestePlusieurs: 'Contesté sur plusieurs points',
    conteste: 'Contesté',
    fractures: 'Pourquoi personne ne s\'entend',
    debatNeDitPas: 'Ce que le débat ne dit pas',
    nbPerspectives: '{n} perspectives sur la même affaire',
    cliquez: 'Cliquez sur une perspective pour lire son analyse détaillée',
    lirePerspective: 'Lire la perspective : {nom}',
    ceQuiSestPasse: 'Ce qui s\'est passé',
    pourAllerPlusLoin: 'Pour aller plus loin',
  },

  subActeurs: {
    segments: {
      visibles: 'Visibles',
      caches: 'Cachés',
      victimes: 'Victimes',
    },
    aucun: { one: 'Des réseaux ont été cherchés derrière le récit : il n\'en compte aucun.', other: 'Des réseaux ont été cherchés derrière les {n} récits : aucun n\'en compte.' },
    tous: { one: 'Des réseaux ont été cherchés derrière le récit : il en compte.', other: 'Des réseaux ont été cherchés derrière les {n} récits : tous en comptent.' },
    partiel: 'Des réseaux ont été cherchés derrière les {n} récits : {avec} en comptent, {sans} aucun. Le détail est dans l\'onglet Perspectives, récit par récit.',
    discret: 'Discret',
    victime: 'Victime',
    agitPlus: 'Agit plus qu\'il ne se voit · +{n}',
    influence: 'Influence',
    visibilite: 'Visibilité',
    retourActeurs: '← Retour aux acteurs',
    position: 'Position dans l\'affaire',
    etabli: 'Ce qui est établi',
    role: 'Rôle',
    indicateurs: 'Indicateurs',
    titre: 'Acteurs',
    sousTitre: 'Visibles · Cachés · Victimes · Bénéficiaires',
    typeActeurs: 'Type d\'acteurs',
    repartition: 'Ce que montre cette répartition',
    echelle: 'Influence et visibilité notées sur 100 — l\'influence est le poids sur le cours des faits, la visibilité la place tenue dans le débat public.',
    dynamique: 'La dynamique de pouvoir, en une lecture',
    cadrage: 'Qui tient le cadrage du débat',
  },

  sub: {
    chronologie: 'Chronologie',
    nbEvenements: '{n} événements',
    peuConnus: ' · {n} peu connus',
    peuConnu: 'Peu connu',
    historique: 'Profondeur historique',
    precedents: '{n} précédents qui éclairent autrement',
    similitudes: 'Similitudes',
    differences: 'Différences',
    lecons: 'Leçons pour le présent',
    benefice: 'Bénéfice',
    impact: 'Impact',
    impactNegatif: 'Impact négatif',
    quoi: 'Ce qui s\'est passé',
    pourquoi: 'Pourquoi ça les touche',
    auBout: 'Au bout du compte',
    tousProfils: '← Tous les profils',
    severite: 'Sévérité d\'impact',
    pays: 'Pays',
    impactCitoyen: 'Impact citoyen',
    profilsDans: '{n} profils concrets dans {pays} pays',
    vueEnsemble: 'Vue d\'ensemble',
    plusTouche: 'Profils — du plus touché au moins touché',
    lireHistoire: 'Lire son histoire',
    plateformes: {
      autres: 'Autres plateformes',
    },
    ecart: {
      separees: 'Deux conversations séparées',
      differents: 'Cadrages nettement différents',
      divergences: 'Quelques divergences',
      proches: 'Cadrages proches',
    },
    reseaux: 'Ce qu\'en disent les réseaux',
    ressentis: '{n} ressentis dominants',
    recitsEmergents: ' · {n} récits émergents',
    misesEnGarde: ' · {n} mises en garde',
    ecartMedias: 'Écart entre les médias et les réseaux — {ecart}/100 · {libelle}',
    ecartAria: 'Écart de cadrage : {ecart} sur 100',
    memesTermes: 'Mêmes termes que les médias',
    domine: 'Ce qui domine en ligne',
    parts: 'Parts du ressenti exprimé en ligne, telles que l’analyse les répartit sur 100.',
    presence: 'Présence de chaque ressenti, mesurée séparément : ces valeurs ne se partagent pas un total.',
    fausser: 'Ce qui peut fausser ces signaux',
    recitsMontent: '{n} récits qui montent',
    parPlateforme: 'Par plateforme — {liste}',
    scenariosPossibles: 'Scénarios possibles',
    scenariosSous: '{n} scénarios possibles · probabilités estimées · déclencheurs',
    declencheurs: 'Déclencheurs possibles',
    implications: 'Implications',
    parProbabilite: 'Par probabilité décroissante',
    sources: 'Sources',
    sourcesSous: '{n} sources · {cats} catégories · transparence méthodologique',
    categorieAria: 'Catégorie de sources',
    corpus: '{n} sources · score moyen {score}/10',
    duCorpus: ' · {part} du corpus',
    biaisEleveCertaines: ' · certaines sources à biais élevé — voir détail',
    contreCourant: 'À contre-courant',
    aucuneSource: 'Aucune source dans cette catégorie.',
    limitesCorpus: 'Limites de ce corpus',
  },

  sourcesCat: {
    primarySources: 'Sources primaires',
    mainstreamWestern: 'Mainstream occidental',
    alternativeIndependent: 'Indépendant / alternatif',
    nonWesternPerspectives: 'Non-occidental',
    academicWitnesses: 'Académique',
  },

  org: {
    portee: {
      global: 'Portée mondiale',
      regional: 'Portée régionale',
      national: 'Portée nationale',
    },
    chargement: 'Chargement…',
    nonTrouvee: 'Organisation non trouvée',
    retour: '← Retour aux organisations',
    organisations: 'Organisations',
    organisation: 'Organisation',
    membresDocumentes: '{n} membres documentés',
    dontBarometre: ' · dont {n} avec un baromètre',
    fondeEn: 'Fondé en {date}',
    explorerReseau: 'Explorer son réseau dans le graphe →',
    metaDescription: 'Think tanks, lobbies, fondations — cartographie de leurs membres, financements et objectifs réels.',
    metaIntrouvable: 'Organisation introuvable',
    metaProfil: '{type} — Réseau d\'influence, membres et financement analysés par Lumière Noire.',
    tiers: {
      S: 'Influence maximale',
      A: 'Influence majeure',
      B: 'Influence significative',
      C: 'Influence modérée',
    },
    statutsMembre: {
      founder: 'Fondateur',
      core_member: 'Membre',
      alumni: 'Alumni',
      young_leader: 'Young Leader',
      invitee: 'Invité',
      honorary: 'Honoraire',
      emerging: 'Émergent',
      coopted_opponent: 'Opposant coopté',
      emerging_leader: 'Leader émergent',
    },
    categories: {
      politicalLeaders: 'Politique',
      corporateLeaders: 'Corporate',
      financialSector: 'Finance',
      mediaProfessionals: 'Médias',
      academicIntellectuals: 'Académique',
      militaryDefense: 'Défense',
    },
    acces: {
      open: 'Ouvert',
      restricted: 'Restreint',
      closed: 'Fermé',
      chatham_house_rule: 'Chatham House Rule',
    },
    q: {
      quoi: 'Qu’est-ce que c’est ?',
      quoiSous: 'Ce qu’elle dit d’elle-même, ce qu’elle poursuit, d’où elle vient.',
      membres: 'Qui en fait partie ?',
      membresSous: '{n} membres documentés, par catégorie ; les invités et les recrues.',
      membresSousUn: '{n} membre documenté, par catégorie ; les invités et les recrues.',
      membresSousVide: 'Les membres documentés, les invités et les recrues.',
      influence: 'Comment pèse-t-elle sur les décisions ?',
      influenceSous: 'Les recommandations suivies d’effet, les crises vues venir, les rendez-vous.',
      argent: 'D’où vient l’argent ?',
      argentSous: 'Qui la finance, comment l’argent circule, les conflits d’intérêts.',
      reseau: 'Avec qui travaille-t-elle ?',
      reseauSous: 'Les organisations liées, les membres qu’elles partagent.',
      bilan: 'Que faut-il en retenir ?',
      bilanSous: 'Les constats, ce qui pose problème, ce qui relève d’une contribution légitime.',
      ouvert: 'Qu’est-ce qui reste ouvert ?',
      ouvertSous: 'Ce que la fiche n’a pas pu établir, la fiabilité de ses sources, les pistes d’enquête.',
      sources: 'Sur quoi ça repose ?',
      sourcesSous: { one: '{n} source au registre de la fiche.', other: '{n} sources au registre de la fiche.' },
      rassemble: 'Cette question rassemble {liste}.',
      rassembleFace: 'Cette question rassemble {liste}, mis face à face.',
      rassembleReseau: 'Cette question rassemble {liste} et les membres qu’elles partagent.',
      recommandations: { one: '{n} recommandation suivie d’effet', other: '{n} recommandations suivies d’effet' },
      crises: { one: '{n} crise vue venir', other: '{n} crises vues venir' },
      evenements: { one: '{n} rendez-vous documenté', other: '{n} rendez-vous documentés' },
      financements: { one: '{n} source de financement', other: '{n} sources de financement' },
      circuit: 'un circuit de financement circulaire documenté',
      conflits: { one: '{n} conflit d’intérêts documenté', other: '{n} conflits d’intérêts documentés' },
      liens: { one: '{n} organisation liée', other: '{n} organisations liées' },
      constats: { one: '{n} constat clé', other: '{n} constats clés' },
      problemes: { one: '{n} aspect problématique', other: '{n} aspects problématiques' },
      contributions: { one: '{n} contribution légitime', other: '{n} contributions légitimes' },
      lesRendezVous: 'Les rendez-vous',
      essentiel: 'L’essentiel',
      sommaire: 'Sommaire de la fiche',
      enNQuestions: 'La fiche en {n} questions',
    },
  },

  menu3: {
    identite: 'Identité',
    influence: 'Influence',
    faits: 'Les faits',
    contexte: 'Le contexte',
  },

  orgSecteurs: {
    politicalLeaders: 'Politique',
    corporateLeaders: 'Corporate',
    financialSector: 'Finance',
    mediaProfessionals: 'Médias',
    academicIntellectuals: 'Académique',
    internationalOrganizations: 'Int. orgs',
    militaryDefense: 'Défense',
  },

  orgMembres: {
    saFiche: 'Sa fiche →',
    documentes: 'Documentés',
    politiques: 'Politiques',
    corporate: 'Corporate',
    finance: 'Finance',
    tauxPlacement: 'Taux placement',
    delaiPosition: 'Délai position',
    conversion: 'Conversion',
    notreLecture: '{libelle} (notre lecture)',
    chercher: 'Chercher un membre…',
    categorie: 'Catégorie',
    periode: 'Période',
    tous: 'Tous',
    enExercice: 'En exercice',
    anciens: 'Anciens',
    nbMembres: { one: '{n} membre', other: '{n} membres' },
    jusquEn: 'jusqu\'en {annee}',
    ancien: 'ancien',
    invitesStrategiques: 'Invités stratégiques',
    invite: 'Invité',
    recruteEnsuite: 'recruté ensuite',
    nonRecrute: 'non recruté',
    opposantsCooptes: 'Opposants cooptés',
    opposantCoopte: 'Opposant coopté',
    positionInitiale: 'Position initiale : {texte}',
    leadersEmergents: 'Leaders émergents cultivés',
    leaderEmergent: 'Leader émergent',
  },

  orgEvents: {
    aucun: 'Aucun événement documenté',
    prochainRun: 'Les événements seront disponibles après le prochain run pipeline.',
    documentesSur: 'Événements documentés sur {n} ans',
    documentes: 'Événements documentés',
    avecParticipants: 'Avec participants identifiés',
    fermees: 'Réunions fermées',
    dateInconnue: 'Date inconnue',
    sansEvenement: '{n} ans sans événement documenté',
    annees: 'Années {decennie}',
    voirEvenements: { one: 'Voir {n} événement de plus', other: 'Voir {n} événements de plus' },
    dateIncertaine: 'Date incertaine',
    nbParticipants: '{n} participants',
    decisionsCorrelees: { one: '→ {n} décision corrélée', other: '→ {n} décisions corrélées' },
    sujetsOfficiels: 'Sujets officiels',
    sujetsNonDivulgues: 'Sujets non divulgués',
    participants: 'Participants',
    nonConfirme: 'Non confirmé',
    autres: { one: '+{n} autre — voir le détail →', other: '+{n} autres — voir le détail →' },
  },

  sourceRef: {
    retireeTitre: 'L\'adresse citée pour cette entrée ne correspondait à aucun format d\'article valide du site invoqué : elle a été retirée. L\'appartenance reste affichée, mais elle n\'est plus adossée à rien.',
    atteste: 'Une source atteste cette appartenance, mais aucune adresse précise ne peut être citée pour cette entrée.',
    nonEtablieTitre: 'Aucune source consultée n\'atteste cette appartenance. L\'entrée est conservée, mais elle n\'est pas vérifiable en l\'état.',
    adresseRetiree: 'adresse retirée',
    attesteSansLien: 'attesté, sans lien',
    nonEtablie: 'source non établie',
    pageAccueilTitre: '{url} — page d\'accueil du site : elle ne documente pas cette affirmation en particulier.',
    pageAccueil: 'page d\'accueil',
    capture: 'Capture archive.org',
    captureDisparue: ' — la page d\'origine ne répond plus (404)',
    voirPlus: 'Voir plus →',
  },

  blocs: {
    nature: {
      faitEtLecture: 'Fait et lecture',
      fait: 'Fait',
      lecture: 'Lecture',
      methode: 'Méthode',
    },
    canaux: {
      titre: 'Les canaux',
      sous: 'Par où passe son influence : les rôles officiels, les relais informels, les allers-retours de personnes.',
      governmentCoordination: 'Auprès des gouvernements',
      corporateCoordination: 'Auprès des entreprises',
      internationalCoordination: 'À l’international',
      officialAdvisoryRoles: 'Rôles consultatifs officiels',
      informalInfluence: 'Influence informelle',
      revolvingDoor: 'Porte tournante',
      legislativeInfluence: 'Sur la loi',
      ceoForums: 'Forums de dirigeants',
      industryAlignment: 'Alignement des secteurs',
      regulatoryCapture: 'Capture réglementaire',
      multilateralInfluence: 'Institutions multilatérales',
      treatyInfluence: 'Traités',
      globalGovernance: 'Gouvernance mondiale',
    },
    argent: {
      titre: 'Où va l’argent',
      sous: 'Les grands postes de dépense, tels que documentés ; les montants sont ceux des sources, souvent arrondis.',
      total: 'Dépenses totales : {liste}',
      operational: 'Fonctionnement',
      programs: 'Programmes',
      events: 'Événements',
      lobbying: 'Lobbying',
    },
    reseau: {
      position: 'Sa position',
      positionSous: 'Lecture de la carte : où elle se situe, ce qu’elle relie.',
      coordination: 'Comment elles se coordonnent',
      coordinationSous: 'Ce qui fait agir ces organisations ensemble.',
      centralityScore: 'Sa place dans le réseau',
      clusterBelonging: 'Les cercles auxquels elle appartient',
      bridgingRole: 'Son rôle de passerelle',
      redundancy: 'La redondance des liens',
      temporalCoordination: 'Dans le temps',
      thematicCoordination: 'Par les thèmes',
      personnelRotation: 'Par les personnes',
      fundingCoordination: 'Par l’argent',
    },
    effets: {
      titre: 'Ce que ça produit',
      sous: 'Notre lecture des effets de son action, à partir des éléments sourcés de la fiche.',
      economicImpact: 'Sur l’économie',
      socialImpact: 'Sur la société',
      sovereigntyImpact: 'Sur la souveraineté',
      democraticImpact: 'Sur la démocratie',
    },
    ouvert: {
      limites: 'Les limites des données',
      limitesSous: 'Ce que la fiche n’a pas pu établir, et pourquoi.',
      appui: 'Sur quoi s’appuyer',
      appuiSous: 'L’évaluation que l’analyse fait de ses propres sources.',
      pistes: 'Les pistes d’enquête',
      pistesSous: 'Ce qu’il faudrait vérifier, et qu’une fiche ne peut pas trancher seule.',
      qualiteSources: 'Qualité des sources',
      completude: 'Complétude des données',
      risqueBiais: 'Risque de biais',
      niveau: {
        high: 'élevé',
        medium: 'moyen',
        low: 'faible',
      },
      niveauF: {
        high: 'élevée',
        medium: 'moyenne',
        low: 'faible',
      },
      techniques: {
        sourceurl: 'Membres sans adresse de source',
        urls: 'Entrées sans adresse de source',
        coopted: 'Opposants cooptés',
      },
    },
  },

  orgLibelles: {
    fonction: {
      network_coordination: 'coordination de réseau',
      financial_coordination: 'coordination financière',
      policy_development: 'élaboration de politiques',
      talent_pipeline: 'vivier de dirigeants',
    },
    capture: {
      systematic: 'systématique',
      significant: 'importante',
      moderate: 'modérée',
    },
    impact: {
      severe: 'grave',
      significant: 'important',
      moderate: 'modéré',
    },
    portee: {
      global: 'mondiale',
      regional: 'régionale',
      national: 'nationale',
    },
    gravite: {
      critical: 'critique',
      high: 'élevée',
      medium: 'moyenne',
      low: 'faible',
    },
    financement: {
      membership_fees: 'cotisations des membres',
      event_revenue: 'recettes des événements',
      foundation_grants: 'subventions de fondations',
      government_funding: 'financement public',
      corporate_partnerships: 'partenariats d’entreprises',
      corporate_operations: 'activité commerciale',
      product_revenue: 'ventes de produits',
    },
  },

  portrait: {
    mission: 'Mission officielle',
    objectifs: 'Objectifs réels',
    tauxPlacement: 'Taux placement',
    orgsLiees: 'Orgs liées',
    conversion: 'Conversion',
    classification: 'CLASSIFICATION SYSTÉMIQUE',
    fonction: 'Fonction',
    capture: 'Capture',
    impactDemocratique: 'Impact démocratique',
    portee: 'Portée',
    phases: 'Phases d\'évolution',
    fondation: 'Fondation',
    expansion: 'Expansion',
    maturite: 'Maturité',
    contexteFondation: 'Contexte de fondation',
    momentHistorique: 'Moment historique',
    recitOfficiel: 'Récit officiel',
    motivations: 'Motivations réelles',
    financementInitial: 'Financement initial',
    soutien: 'Soutien institutionnel',
    fondateurs: 'Fondateurs ({n})',
    fondateur: 'Fondateur',
    reduire: 'Réduire ↑',
    details: 'Détails →',
    motivationsAnalysees: 'Motivations analysées',
    connexions: 'Connexions réseau',
    trajectoire: 'Trajectoire post',
    structure: 'Structure organisationnelle',
    gouvernance: 'Gouvernance',
    criteres: 'CRITÈRES DE SÉLECTION',
    categoriesMembres: 'CATÉGORIES DE MEMBRES',
    transparence: 'NIVEAU DE TRANSPARENCE',
    employes: 'Employés',
    budget: 'Budget annuel',
  },

  duree: {
    immediat: 'immédiat',
    mois: { one: '{n} mois', other: '{n} mois' },
    ans: { one: '{n} an', other: '{n} ans' },
    environAns: '~{n} ans',
    jusqua: 'jusqu\'à {d}',
    entre: 'entre {a} et {b}',
  },

  panneau: {
    presse: {
      closed: 'à huis clos',
      chatham_house_rule: 'règle de Chatham House',
      open: 'ouvert à la presse',
      partial: 'presse en partie admise',
    },
    source: 'Source',
    aQuitteEn: 'a quitté en {annee}',
    ancienMembre: 'ancien membre',
    inviteEn: 'invité : {annee}',
    recruteEnsuite: 'recruté ensuite',
    nonRecrute: 'non recruté',
    'cooptéEn': 'coopté en {annee}',
    repereEn: 'repéré en {annee}',
    positionActuelle: 'Position actuelle',
    plusHautPoste: 'Plus haut poste',
    posteEntree: 'Poste à l\'entrée',
    poste: 'Poste',
    positionInitiale: 'Position initiale',
    apresCooptation: 'Après cooptation',
    loyaute: 'Loyauté',
    anneeEntree: 'Année d\'entrée',
    anneeSortie: 'Année de sortie',
    alignement: 'Alignement politique',
    decisionsCles: 'Décisions clés',
    utilisationReseau: 'Utilisation du réseau',
    contexteInvitation: 'Contexte d\'invitation',
    gainOrganisation: 'Ce que l’organisation y gagne',
    relationEnsuite: 'Relation ensuite',
    strategieCooptation: 'Stratégie de cooptation',
    neutralisation: 'Neutralisation',
    strategieSuivi: 'Stratégie de suivi',
    placementObtenu: 'Placement obtenu',
    description: 'Description',
    preuvesCoordination: 'Preuves de coordination',
    membresPartages: 'Membres partagés notables',
    organisations: 'Organisations',
    fonctionPont: 'Fonction de pont',
    doubleAppartenance: 'Ce que la double appartenance lui apporte',
    adoptee: 'adoptée',
    delaiImplementation: 'Délai implémentation',
    impactEconomique: 'Impact économique',
    gouvernementsCibles: 'Gouvernements cibles',
    preuvesDocumentees: 'Preuves documentées',
    beneficiaires: 'Bénéficiaires',
    alumniImplementeurs: 'Alumni implémenteurs',
    gravite: 'gravité {niveau}',
    acteursImpliques: 'Acteurs impliqués',
    preuves: 'Preuves',
    premiereDiscussion: 'Première discussion',
    criseRealisee: 'Crise réalisée',
    delaiAnticipation: 'Délai anticipation',
    mesuresPreparation: 'Mesures de préparation',
    nbParticipants: '{n} participants',
    participants: 'Participants ({n})',
    nonConfirme: 'non confirmé',
    decisionsCorrelees: 'Décisions corrélées ({n})',
    pasCausalite: 'Corrélation temporelle documentée et sourcée — pas une causalité établie.',
    suiviImmediat: 'suivi immédiatement',
    suiviApres: 'suivi {d} plus tard',
  },

  orgOnglets: {
    deductionTitre: 'Déduction de Lumière Noire à partir des éléments sourcés de cette fiche — pas un fait rapporté par une source.',
    notreLecture: 'notre lecture',
    source: 'source ↗',
    recos: {
      transparencyMeasures: 'Mesures de transparence',
      democraticSafeguards: 'Garde-fous démocratiques',
    },
    verdictSystemique: 'Verdict systémique',
    anomalies: 'Anomalies statistiques',
    constatsCles: 'Constats clés',
    problematiques: 'Aspects problématiques',
    legitimes: 'Contributions légitimes',
    recommandations: 'Recommandations',
    gravite: 'gravité {niveau}',
    sourcesFinancement: 'Sources de financement',
    circulaire: 'Modèle de financement circulaire',
    circuitCapture: 'Circuit de capture documenté',
    roi: 'ROI estimé',
    conflits: 'Conflits d\'intérêts ({n})',
    transparence: 'Transparence',
    manquements: { one: '{n} manquement documenté', other: '{n} manquements documentés' },
    adopte: '✓ Adopté',
    nonAdopte: 'Non adopté',
    recommandationsStat: 'Recommandations',
    tauxAdoption: 'Taux adoption',
    delaiMoyen: 'Délai moyen',
    penetrationGeo: 'Pénétration géo',
    recosPolitiques: 'Recommandations politiques',
    anticipationCrises: 'Anticipation de crises',
    orgLiee: 'Organisation liée',
    nbOrganisations: '{n} organisations',
    orgsLiees: { one: '{n} organisation liée', other: '{n} organisations liées' },
    ecosysteme: 'Évaluation de l\'écosystème',
    organisationsLiees: 'Organisations liées ({n})',
    laFiche: 'La fiche →',
    multiAffilies: 'Membres multi-affiliés ({n})',
    multiAffiliesTexte: 'Des personnes présentes dans plusieurs organisations de l\'écosystème : les nœuds de coordination entre structures.',
    aucuneSource: 'Aucune source documentée pour cette organisation pour l\'instant.',
    sourcesDocumentees: 'Sources documentées',
    avecLien: '{n} sources · {liens} avec lien direct',
    tier: 'Tier {tier}',
  },

  orgOrientation: {
    liens: {
      shared_members: 'membres partagés',
      funding: 'financement',
      coordination: 'coordination',
      ideological_alignment: 'alignement idéologique',
      subsidiary: 'filiale',
      parent: 'maison mère',
    },
    titre: 'Fiche d\'orientation — pas une analyse',
    etablissent: 'Ce que {n} de nos analyses établissent',
    sansAdresse: 'Cette mention ne porte aucune adresse de source.',
    personnes: 'Personnes que nos analyses rattachent à {nom}',
    personnesTexte: 'Elles apparaissent dans nos analyses d\'autres organisations. Ce ne sont pas nécessairement ses dirigeants, et la liste n\'est pas exhaustive.',
    releveDans: 'relevé dans notre analyse de',
    nePeutPas: 'Ce que cette fiche ne peut pas dire',
    assemblee: 'Assemblée le {date} à partir de {mentions} mention(s) et {sources} source(s). Aucune recherche propre, aucun contenu généré.',
  },

  orgListe: {
    portees: {
      global: 'Global',
      regional: 'Régional',
      national: 'National',
    },
    tous: 'Tous',
    toutes: 'Toutes',
    kicker: 'Organisations · réseaux d\'influence',
    orientations: { one: ' · {n} fiche d\'orientation', other: ' · {n} fiches d\'orientation' },
    titre: 'Les cercles du pouvoir',
    analysees: { one: 'organisation analysée', other: 'organisations analysées' },
    recherche: 'Nom, sigle, siège…',
    type: 'Type',
    portee: 'Portée',
    analyseesDabord: 'Analysées d’abord',
    plusMembres: 'Plus de membres documentés',
    nomAZ: 'Nom, de A à Z',
    aucune: 'Aucune organisation ne correspond',
    bientot: 'Les premières organisations cartographiées arrivent bientôt.',
    bleuPortee: 'Le bleu suit la portée :',
    mondiale: 'mondiale',
    regionale: 'régionale',
    nationale: 'nationale',
    nonRenseignee: 'non renseignée',
    membresDocumentes: 'membres documentés',
    ficheOrientation: 'Fiche d\'orientation',
    citeePar: ' · citée par {noms}',
  },

  cumulards: {
    aria: 'Les personnes présentes dans plusieurs organisations',
    titre: { one: '{n} personne dans plusieurs de ces organisations', other: '{n} personnes dans plusieurs de ces organisations' },
    plusPresentes: ' · les {n} plus présentes',
    nbOrganisations: '{n} organisations',
  },

  cartes: {
    ouvrir: '{nom} — ouvrir le détail',
    detail: 'Détail →',
  },

  enq: {
    nature: {
      lecture: 'Lecture',
      hypothese: 'Hypothèse',
      observation: 'Observation',
      fait: 'Fait',
    },
    source: 'Source ↗',
    plusLoin: 'Pour aller plus loin',
    statuts: {
      en_cours: 'Procédure en cours',
      classe: 'Classée',
      close: 'Close',
      condamnation: 'Condamnation',
      relaxe: 'Relaxe',
      non_lieu: 'Non-lieu',
    },
    declare: 'Déclaré',
    observe: 'Observé',
    estime: 'Estimé',
    ecart: 'Écart',
    attendu: 'Attendu',
    intention: 'Intention',
    coincidence: 'Coïncidence',
    systeme: 'Système',
    q: {
      qui: 'Qui est-ce ?',
      quiSous: 'Le parcours, étape par étape.',
      justice: 'Qu’examine la justice ?',
      justiceSous: 'Les procédures documentées et leur statut. Une procédure en cours n’établit aucune culpabilité.',
      argent: 'D’où vient l’argent ?',
      argentSous: 'Les flux documentés, le patrimoine, les conflits d’intérêts.',
      argentReserve: { one: '{n} flux ou conflit d’intérêts documenté, le patrimoine déclaré et estimé, le train de vie.', other: '{n} flux et conflits d’intérêts documentés, le patrimoine déclaré et estimé, le train de vie.' },
      entourage: 'Qui l’entoure ?',
      entourageSous: 'Les proches, les alliés, les relations d’affaires, de presse et d’influence.',
      entourageReserve: { one: '{n} relation documentée : proches, alliés politiques, affaires, médias, cercles d’influence.', other: '{n} relations documentées : proches, alliés politiques, affaires, médias, cercles d’influence.' },
      recit: 'Qui raconte son histoire ?',
      recitSous: 'La presse qui le porte, ce qui est amplifié, ce qui disparaît.',
      recitReserve: { one: '{n} mécanisme ou récit documenté : la presse proche, les sujets amplifiés, les sujets écartés.', other: '{n} mécanismes et récits documentés : la presse proche, les sujets amplifiés, les sujets écartés.' },
      colle: 'Qu’est-ce qui ne colle pas ?',
      colleSous: 'Ce qui a été dit et ce qui a été fait ; ce qui s’écarte de l’ordinaire.',
      colleReserve: { one: '{n} contradiction ou écart documenté, confronté point par point.', other: '{n} contradictions et écarts documentés, confrontés point par point.' },
      ouvert: 'Qu’est-ce qui reste ouvert ?',
      ouvertSous: 'Ce que l’enquête n’a pas pu trancher, et ce qu’elle a écarté.',
      ouvertReserve: { one: '{n} autre question ou rumeur écartée, et les limites de l’enquête.', other: '{n} autres questions et rumeurs écartées, et les limites de l’enquête.' },
      sources: 'Sur quoi ça repose ?',
      sourcesSous: { one: '{n} source, dans l’ordre où l’enquête la cite.', other: '{n} sources, dans l’ordre où l’enquête les cite.' },
    },
    blocs: {
      formation: 'Formation',
      parcoursPrive: 'Parcours privé',
      chaqueEtape: 'Ce que l’enquête relève à chaque étape',
      enquetesEnCours: 'Enquêtes en cours',
      recoupement: 'Le recoupement, affaire par affaire',
      refutation: 'Éléments de réfutation',
      conflits: 'Conflits d’intérêts documentés',
      patrimoine: 'Patrimoine',
      transactions: 'Transactions marquantes',
      construction: 'La construction, période par période',
      trainDeVie: 'Train de vie et biens',
      premierCercle: 'Le premier cercle',
      politique: 'En politique',
      affaires: 'Dans les affaires',
      presse: 'Dans la presse',
      international: 'À l’international',
      cerclesInfluence: 'Cercles d’influence',
      liensInattendus: 'Liens inattendus',
      cercleRapproche: 'Le cercle rapproché, rôle officiel et rôle réel',
      nominations: 'Comment il nomme',
      protection: 'Protection institutionnelle',
      opposants: 'Ses opposants',
      amplifie: 'Ce qui est amplifié',
      disparait: 'Ce qui disparaît',
      favorables: 'Médias décrits comme favorables',
      critiques: 'Médias décrits comme critiques',
      ordinaire: 'Ce qui s’écarte de l’ordinaire',
      schemas: 'Les schémas qui reviennent, et trois façons de les lire',
      hypotheses: 'Hypothèses concurrentes',
      rumeursEcartees: 'Rumeurs écartées faute de recoupement',
      manque: 'Ce qui manque pour trancher',
      biais: 'Les biais possibles de cette enquête',
    },
    documentOfficiel: 'Document officiel',
    essentiel: 'L’essentiel',
    sommaire: 'Sommaire de l’enquête',
    enNQuestions: 'L’enquête en {n} questions',
    legendeNatures: 'chaque passage dit ce qu’il est.',
    faitsDocumentes: { one: '{n} fait documenté, à rebours de l’idée reçue.', other: '{n} faits documentés, à rebours de l’idée reçue.' },
    page: {
      acteurNonTrouve: 'Acteur non trouvé',
      introuvable: 'Investigation introuvable',
      nonDisponibles: 'Données non disponibles',
      retour: '← Retour à la fiche acteur',
      investigation: 'Investigation',
      kicker: 'Investigation · mise à jour le {date}',
      procedures: { one: 'procédure judiciaire documentée', other: 'procédures judiciaires documentées' },
    },
  },

  anatInv: {
    force: {
      strong: 'Fort',
      moderate: 'Modéré',
      weak: 'Faible',
    },
    certitude: {
      high: 'Certitude élevée',
      medium: 'Certitude moyenne',
      low: 'Certitude faible',
    },
    fiabilite: {
      high: 'Fiabilité élevée',
      medium: 'Fiabilité moyenne',
      low: 'Fiabilité faible',
    },
    piste: {
      mechanism_to_expose: 'Mécanisme à exposer',
      relationship_to_document: 'Relation à documenter',
      fact_to_verify: 'Fait à vérifier',
      timeline_to_reconstruct: 'Chronologie à reconstituer',
      actor_to_investigate: 'Acteur à investiguer',
    },
    priorite: {
      high: 'priorité haute',
      medium: 'priorité moyenne',
      low: 'priorité basse',
    },
    prioriteBrute: 'priorité {p}',
    acteursCles: 'Acteurs clés',
    orgsCles: 'Organisations clés',
    aucunFait: 'Aucun fait stabilisé pour ce thème.',
    faitsPivots: { one: 'Le fait pivot documenté qui fonde l’enquête.', other: 'Les {n} faits pivots documentés qui fondent l’enquête.' },
    aucunArgument: 'Aucun argument disponible.',
    pour: 'Pour',
    contre: 'Contre',
    aucunPour: 'Aucun argument pour.',
    aucunContre: 'Aucun argument contre.',
    sourceFleche: 'Source →',
    aucunAngle: 'Aucun angle mort identifié.',
    anglesIntro: 'Les zones d’ombre et questions laissées ouvertes par l’état actuel de la documentation.',
    aucuneChrono: 'Aucune donnée chronologique ni piste identifiée.',
    chronologie: 'Chronologie ({n})',
    pistes: 'Pistes à creuser ({n})',
    pistesIntro: 'Ce que l’enquête identifie comme prochains angles d’investigation.',
    faits: 'Faits',
    arguments: 'Arguments',
    anglesMorts: 'Angles morts',
    chronoPistes: 'Chrono & Pistes',
    pasDeDetail: 'Pas encore de détail documenté pour ce thème.',
    question: 'La question posée',
    conclut: 'Ce qu’elle conclut',
    appuie: 'Ce qui l’appuie',
    limite: 'Ce qui la limite',
  },

  preuve: {
    P1: 'Fait établi',
    P2: 'Fait probable',
    P3: 'Hypothèse étayée',
    P4: 'Hypothèse plausible',
    P5: 'Spéculation',
  },

  anatQ: {
    certitude: {
      high: 'certitude élevée',
      medium: 'certitude moyenne',
      low: 'certitude faible',
      contested: 'certitude contestée',
    },
    implication: {
      structural: 'Structurel',
      political: 'Politique',
      economic: 'Économique',
      social: 'Social',
    },
    essentiel: 'Que faut-il retenir ?',
    essentielSous: 'Ce que l’enquête affirme, ce qu’elle démontre, ce qui en découle.',
    affirme: 'Ce qui est affirmé',
    demontre: 'Ce qui est démontré · {n}',
    decoule: 'Ce qui en découle',
    enquetes: 'Qu’a-t-on enquêté ?',
    enquetesSous: { one: '{n} sous-enquête : la question posée, ce qu’elle conclut, les faits et les arguments.', other: '{n} sous-enquêtes : la question posée, ce qu’elle conclut, les faits et les arguments.' },
    enquetesReserve: { one: 'Cette question rassemble {n} sous-enquête, avec sa question, sa conclusion, ses faits, ses arguments et sa chronologie.', other: 'Cette question rassemble {n} sous-enquêtes, chacune avec sa question, sa conclusion, ses faits, ses arguments et sa chronologie.' },
    convergences: 'Qu’est-ce qui revient partout ?',
    convergencesSous: 'Ce que les sous-enquêtes établissent ensemble, et là où elles se contredisent.',
    convergencesReserve: { one: 'Cette question rassemble {n} convergence entre les sous-enquêtes, et leurs contradictions.', other: 'Cette question rassemble {n} convergences entre les sous-enquêtes, et leurs contradictions.' },
    citoyens: 'Qu’est-ce que ça coûte aux citoyens ?',
    citoyensSous: 'Le chiffre à retenir, les ordres de grandeur, les effets directs.',
    citoyensReserve: 'Cette question chiffre ce que le système coûte concrètement : le chiffre à retenir, ses équivalences, les effets directs sur les ménages.',
    acteurs: 'Qui est impliqué ?',
    acteursSous: 'Les acteurs et les organisations, de ceux qu’on retrouve partout à ceux d’une seule sous-enquête.',
    acteursReserve: 'Cette question rassemble les acteurs et les organisations des sous-enquêtes, triés par transversalité.',
    ouvert: 'Que ne peut-on pas prouver ?',
    ouvertSous: 'Ce que les preuves ne permettent pas d’affirmer, et la question qui reste sans réponse.',
    questionOuverte: 'La question qui reste ouverte',
    sources: 'Sur quoi ça repose ?',
    sourcesSous: { one: 'Les documents sur lesquels la sous-enquête s’appuie.', other: 'Les documents sur lesquels les {n} sous-enquêtes s’appuient.' },
    sommaire: 'Sommaire de l’anatomie',
    enNQuestions: 'L’anatomie en {n} questions',
  },

  anatConv: {
    force: {
      strong: 'convergence forte',
      moderate: 'convergence modérée',
      weak: 'convergence faible',
    },
    resolution: {
      resolved: 'Résolue',
      partially_resolved: 'Partiellement résolue',
      non_resolved: 'Non résolue',
    },
    intro: 'Une convergence est un mécanisme qui apparaît indépendamment dans plusieurs sous-enquêtes : le signe qu’il est systémique, pas accidentel.',
    titre: 'Convergences · {n}',
    dontFortes: { one: ', dont {n} forte', other: ', dont {n} fortes' },
    sousEnquetes: { one: '{n} sous-enquête', other: '{n} sous-enquêtes' },
    preuvesCles: 'Preuves clés · {n}',
    source: 'Source →',
    rechercheComplementaire: 'Recherche complémentaire',
    contradictions: 'Contradictions · {n}',
    dontNonResolues: { one: ', dont {n} non résolue', other: ', dont {n} non résolues' },
    ouEnEst: 'Où en est la contradiction',
    concernees: 'Sous-enquêtes concernées',
  },

  anatSrc: {
    fiab: {
      official: 'Document officiel',
      high: 'Fiabilité élevée',
      medium: 'Fiabilité moyenne',
      moderate: 'Fiabilité modérée',
    },
    court: {
      official: 'Officiel',
      high: 'Élevée',
      medium: 'Moyenne',
      moderate: 'Modérée',
    },
    aucune: 'Aucune source n’a été consignée pour cette anatomie.',
    documents: { one: 'document distinct', other: 'documents distincts' },
    dont: 'dont',
    solides: { one: 'officiel ou à fiabilité élevée', other: 'officiels ou à fiabilité élevée' },
    partagees: { one: 'cité par plusieurs thèmes', other: 'cités par plusieurs thèmes' },
    toutes: 'Toutes',
    nonRenseignee: 'Fiabilité non renseignée',
    citePar: 'Cité par {n} thèmes',
  },

  anatCit: {
    echelle: {
      individual: 'Individuel',
      household: 'Ménage',
      community: 'Communauté',
      societal: 'Sociétal',
    },
    chiffres: 'Chiffres clés',
    directs: 'Impacts directs',
    indirects: 'Impacts indirects',
    opportunite: 'Coût d’opportunité',
    geographie: 'Par région',
    aRetenir: 'Le chiffre à retenir',
    synthese: 'Synthèse',
  },

  anatEnt: {
    sousEnquetes: { one: '{n} sous-enquête', other: '{n} sous-enquêtes' },
    conflit: 'Conflit d’intérêts',
    acteur: 'Acteur',
    organisation: 'Organisation',
    comptes: '{total} entités recensées · {transversales} présentes dans au moins deux sous-enquêtes',
    conflits: { one: ' · {n} conflit d’intérêts relevé', other: ' · {n} conflits d’intérêts relevés' },
    centraux: 'Présents dans trois sous-enquêtes ou plus · {n}',
    autresActeurs: 'Les autres acteurs · {n}',
    autresOrgs: 'Les autres organisations · {n}',
  },

  anatPage: {
    certitude: {
      high: 'Certitude élevée',
      medium: 'Certitude moyenne',
      low: 'Certitude faible',
      contested: 'Contesté',
      aucune: 'Non évalué',
    },
    anatomies: 'Anatomies',
    metaDescription: 'Dossiers d’investigation approfondis — thèmes, convergences, acteurs et conclusions sur les sujets qui structurent le monde.',
    kicker: 'Anatomies · grands dossiers transversaux',
    titre: 'Qu’est-ce qui fait système ?',
    grandsDossiers: { one: 'grand dossier', other: 'grands dossiers' },
    intro: 'Une anatomie part d’un sujet, l’ouvre en sous-enquêtes, puis cherche ce qui revient d’une enquête à l’autre : les mêmes noms, les mêmes mécanismes.',
    aucune: 'Aucune anatomie pour l’instant',
    aucuneDesc: 'Les anatomies de discours arrivent au fil des publications.',
    themes: { one: 'thème', other: 'thèmes' },
    hautePriorite: ' · {n} haute priorité',
    convergences: { one: 'convergence', other: 'convergences' },
    erreurReseau: 'Erreur réseau',
    chargement: 'Chargement de l’anatomie…',
    introuvable: 'Anatomie introuvable',
    anatomie: 'Anatomie',
    grandDossier: 'Grand dossier',
    legende: { one: 'sous-enquêtes · {n} convergence', other: 'sous-enquêtes · {n} convergences' },
    quiTraverse: 'Qui traverse les {n} sous-enquêtes',
    pied: 'Lumière Noire · Anatomie · {date}',
    metaDefaut: 'Anatomie approfondie — {n} thèmes investigués. Convergences, acteurs et conclusions par Lumière Noire.',
    ogSous: '{n} thèmes · Anatomie',
  },

  enqHub: {
    dossiers: {
      nom: 'Dossiers',
      accroche: 'Tout réunir',
      description: 'Enquêtes transversales qui connectent analyses, investigations et dépêches autour d’un sujet ou d’une entité centrale.',
      voir: 'Voir les dossiers →',
    },
    anatomies: {
      nom: 'Anatomies',
      accroche: 'Disséquer',
      description: 'Dossiers d’investigation approfondis — thèmes, convergences, acteurs et conclusions sur les sujets qui structurent le monde.',
      voir: 'Voir les anatomies →',
    },
    loupes: {
      nom: 'Loupes',
      accroche: 'Pointer l’anomalie',
      description: 'Anomalies factuelles hiérarchisées sur un sujet d’actualité ou de fond. La Loupe pose des questions, elle ne tranche pas.',
      voir: 'Voir les loupes →',
    },
    portee: {
      affair: 'Affaire',
      period: 'Période',
      event: 'Événement',
    },
    bientot: 'Bientôt les premières publications.',
    titreDoc: 'Enquêtes — Lumière Noire',
    enquetes: 'Enquêtes',
    kicker: 'Enquêtes · investigations longues',
    titre: 'Qu’y a-t-il derrière ?',
    publiees: { one: 'enquête publiée', other: 'enquêtes publiées' },
    intro: 'Nos formats les plus fouillés, là où l’actualité est déjà passée à autre chose. Trois manières de creuser : tout réunir, disséquer un système, ou pointer une anomalie précise.',
  },

  dosListe: {
    ancre: {
      actor: 'Acteur',
      organization: 'Organisation',
      thematic: 'Thématique',
    },
    metaAnalyse: '✦ MÉTA-ANALYSE',
    premium: '✦ Premium',
    stats: {
      analyses: { one: 'analyse', other: 'analyses' },
      investigations: { one: 'investigation', other: 'investigations' },
      bsScores: { one: 'BS', other: 'BS' },
      recentSignals: { one: 'dépêche', other: 'dépêches' },
    },
    tag: 'INTELLIGENCE ÉDITORIALE',
    titre: 'Dossiers',
    sousTitre: 'Enquêtes transversales qui connectent analyses, investigations et dépêches autour d’un sujet ou d’une entité centrale.',
    publies: 'Dossiers publiés',
    avecMeta: 'Avec méta-analyse',
    contenusLies: 'Contenus liés',
    type: 'Type',
    tous: 'Tous',
    compte: { one: '{n} dossier', other: '{n} dossiers' },
    aucun: 'Aucun dossier publié aujourd’hui',
    aucunType: 'Aucun dossier de type « {type} »',
    aucunDesc: 'Un dossier se bâtit sur des analyses déjà publiées. Nous refaisons cette matière avant d’en écrire de nouveaux.',
  },

  dosFiche: {
    statut: {
      confirmed: 'Confirmé',
      invalidated: 'Invalidé',
      evolving: 'En évolution',
      pending: 'En attente',
    },
    confiance: {
      high: 'Forte',
      medium: 'Modérée',
      low: 'Faible',
    },
    gravite: {
      high: 'Critique',
      medium: 'Modérée',
      low: 'Mineure',
    },
    confianceRayon: {
      high: 'Confiance : Élevée',
      medium: 'Confiance : Modérée',
      low: 'Confiance : Faible',
    },
    hypotheses: 'Hypothèses',
    contradictions: 'Contradictions',
    noeuds: 'Nœuds cachés',
    perspectives: 'Perspectives',
    anglesMorts: 'Angles morts',
    metaAnalyse: 'Méta-analyse',
    sources: 'Sources :',
    acteur: 'Acteur',
    organisation: 'Organisation',
    hypotheseInitiale: 'Hypothèse initiale : {texte}',
    investigation: 'Investigation',
    contradictionPrincipale: 'Contradiction principale',
    apparaitDans: { one: 'Apparaît dans {n} analyse', other: 'Apparaît dans {n} analyses' },
    investigationComplete: '→ Investigation complète',
    coherence: '→ Cohérence',
    erreurChargement: 'Erreur de chargement',
    chargement: 'Chargement du dossier…',
    introuvable: 'Dossier introuvable',
    retour: '← Retour aux dossiers',
    syntheseMeta: '✦ Synthèse méta',
    introduction: 'Introduction',
    acteursOrgs: 'Acteurs & organisations',
    depechesRecentes: 'Dépêches récentes',
    dossiers: 'Dossiers',
    metaDisponible: '✦ Méta-analyse disponible',
    baseSur: 'Basé sur',
    analyses: 'Analyses',
    investigations: 'Investigations',
    analysesCoherence: 'Analyses de cohérence',
    misAJour: 'Mis à jour le {date}',
    gate: 'Dossier complet réservé aux abonnés Premium',
    metaPasGeneree: 'La méta-analyse de ce dossier n’a pas encore été générée.',
    rayonsX: 'Rayons X liés',
    analysesAssociees: 'Analyses associées',
    aucuneEntite: 'Aucune entité centrale identifiée pour ce dossier.',
    orgsImpliquees: 'Organisations impliquées',
    aucuneDepeche: 'Aucune dépêche récente pour ce dossier.',
    filEditorial: 'Fil éditorial',
    conclusion: 'Conclusion éditoriale',
  },

  loupe: {
    gravite: {
      cosmetic: 'Cosmétique',
      serious: 'Sérieux',
      structural: 'Structurel',
    },
    preuve: {
      established: 'Établi',
      strongly_indicated: 'Fortement indiqué',
      to_verify: 'À vérifier',
    },
    singularite: {
      banal: 'Banal',
      unusual: 'Inhabituel',
      highly_unusual: 'Très inhabituel',
    },
    registre: {
      procedural: 'Procédural',
      mediatic: 'Médiatique',
      biographic: 'Biographique',
      financial: 'Financier',
      behavioral: 'Comportemental',
      institutional: 'Institutionnel',
    },
    plausible: 'Plausible',
    moinsPlausible: 'Moins plausible',
    evenement: {
      declaration: 'Déclaration',
      procedural_act: 'Acte procédural',
      institutional_event: 'Évènement institutionnel',
      media_event: 'Évènement médiatique',
      biographic_event: 'Évènement biographique',
      financial_flow: 'Flux financier',
      other: 'Autre',
    },
    phase: {
      procedural_act: 'Actes procéduraux',
      declaration: 'Déclarations publiques',
      institutional_event: 'Évènements institutionnels',
      media_event: 'Couverture médiatique',
      biographic_event: 'Évènements biographiques',
      financial_flow: 'Flux financiers',
      other: 'Évènements divers',
    },
    aucunEvenement: 'Aucun évènement daté n’a été placé sur la chronologie.',
    aucunExploitable: 'Aucun évènement daté exploitable.',
    chronoIntro: 'Les évènements documentés par les anomalies du dossier, regroupés par année.',
    nEvenements: { one: '{n} évènement', other: '{n} évènements' },
    typeActeur: {
      person: 'Personne',
      organization: 'Organisation',
      institution: 'Institution',
    },
    aucunActeur: 'Aucun acteur identifié dans ce dossier.',
    acteursIntro: 'Les entités identifiées dans plusieurs anomalies du dossier. Les pivots agrègent leurs relations principales documentées.',
    pivots: 'Les pivots du dossier',
    mentions: { one: 'mention', other: 'mentions' },
    nMentions: { one: '{n} mention', other: '{n} mentions' },
    autresRelations: { one: 'et {n} autre relation', other: 'et {n} autres relations' },
    autresActeurs: 'Les autres acteurs',
  },

  loupeB: {
    refermer: 'Refermer ↑',
    plus: { one: '{n} source, questions et hypothèses ↓', other: '{n} sources, questions et hypothèses ↓' },
    aucuneAnomalie: 'Aucune anomalie dans ce dossier.',
    registre: 'Registre',
    tous: 'Tous',
    tri: 'Tri',
    score: 'Score',
    gravite: 'Gravité',
    aucuneFiltre: 'Aucune anomalie sur ce filtre. Cliquez sur « Tous » pour réinitialiser.',
    portee: {
      affair: 'Affaire',
      period: 'Période',
      event: 'Évènement',
    },
    loupe: 'Loupe',
    anomalies: { one: '{n} anomalie', other: '{n} anomalies' },
    structurelles: { one: ' · {n} structurelle', other: ' · {n} structurelles' },
    sources: { one: ' · {n} source', other: ' · {n} sources' },
    leDossier: 'Le dossier →',
    onglets: {
      saillantes: 'Saillantes',
      toutes: 'Toutes',
      acteurs: 'Acteurs',
      chronologie: 'Chronologie',
      nonaffirme: 'Non-affirmé',
    },
    leDossierAria: 'Le dossier',
    aucuneSaillante: 'Aucune anomalie ne dépasse le seuil de saillance (score composite ≥ {seuil}). Consultez l’onglet « Toutes » pour la liste complète.',
    saillantesIntro: 'Les {n} anomalies les plus marquantes du dossier, sur {total} relevées.',
    sourcesN: 'Sources ({n})',
    questionsOuvertes: 'Questions ouvertes',
    hypothesesPossibles: 'Hypothèses possibles',
    speculatives: 'Hypothèses spéculatives — non démontrées',
    nonAffirme: 'Non-affirmé',
    aucunNonAffirme: 'Aucune mention de conclusion non-affirmée dans ce dossier.',
    nonAffirmeIntro: 'Ce dossier liste des observations factuelles sans conclure. Voici les conclusions que la lecture pourrait suggérer mais que ce dossier refuse explicitement de tirer.',
  },

  loupeP: {
    metaTitre: 'Loupe — Lumière Noire',
    metaDescription: 'Anomalies factuelles hiérarchisées sur des sujets d’actualité ou de fond. Loupe pose des questions, ne tranche pas.',
    enquetes: 'Enquêtes',
    loupe: 'Loupe',
    kicker: 'Loupe · anomalies factuelles',
    titre: 'Qu’est-ce qui ne colle pas ?',
    legende: { one: 'anomalies relevées dans {n} dossier', other: 'anomalies relevées dans {n} dossiers' },
    intro: 'Les points de friction documentables d’une affaire — judiciaires, institutionnels, procéduraux —, sourcés et hiérarchisés, sans conclusion sur le fond. La Loupe pose des questions, elle ne tranche pas.',
    aucune: 'Aucune Loupe pour le moment',
    aucuneDesc: 'Les Loupes hiérarchisent les anomalies factuelles ; les premières arrivent bientôt.',
    introuvable: 'Dossier introuvable — Lumière Noire',
    metaFiche: 'Dossier Loupe : anomalies factuelles hiérarchisées sur « {sujet} ». Aucune conclusion tirée.',
    dossier: 'Dossier',
    legendeFiche: { one: 'anomalies, dont {n} structurelle', other: 'anomalies, dont {n} structurelles' },
    sourcesRegistres: { one: '{sources} sources · {n} registre', other: '{sources} sources · {n} registres' },
    publieLe: 'publié le {date}',
    gate: 'Dossier Loupe réservé aux abonnés Premium',
    construit: { one: 'Dossier construit à partir de {sources} sources web, {n} sous-question d’investigation.', other: 'Dossier construit à partir de {sources} sources web, {n} sous-questions d’investigation.' },
    publieLeMaj: 'Publié le {date}.',
  },

  veille: {
    onglets: {
      all: 'Tout',
      analyses: 'Sujets',
      acteurs: 'Acteurs',
      scenarios: 'Perspectives',
    },
    importance: {
      critical: 'Critique',
      high: 'Élevé',
      medium: 'Moyen',
      low: 'Faible',
    },
    type: {
      micro_event: 'Micro-événement',
      major_evolution: 'Évolution majeure',
      scenario_update: 'Perspective',
      contradiction: 'Contradiction',
      new_relationship: 'Nouvelle relation',
      obsolescence: 'Obsolescence',
    },
    minutes: '{n}min',
    heures: '{n}h',
    jours: '{n}j',
    jour: {
      avenir: 'À venir',
      aujourdhui: 'Aujourd’hui',
      hier: 'Hier',
      semaine: 'Cette semaine',
      mois: 'Ce mois',
      ancien: 'Plus ancien',
    },
    critique: 'Critique',
    sources: 'Sources',
    badge: {
      actor: 'Acteur',
      scenario: 'Perspective',
      analysis: 'Sujet',
    },
    recents: { one: '{n} récent', other: '{n} récents' },
    critiques: { one: '{n} critique', other: '{n} critiques' },
    voirFiche: 'Voir la fiche',
    depeches: { one: '{n} dépêche', other: '{n} dépêches' },
    titreDoc: 'Actualité — Lumière Noire',
    plusDeSignaux: '{n} signaux de plus · {reste} restants',
    actualite: 'Actualité',
    kickerDate: 'Veille · dernière dépêche le {date}',
    kicker: 'Veille',
    titre: 'Qu’est-ce qui a bougé ?',
    legende: 'dépêches en 30 jours',
    dontCritiques: ' · dont {n} critiques',
    depechesOnglet: 'Dépêches ({n})',
    entitesOnglet: 'Entités ({n})',
    chargement: 'Chargement…',
    aucunResultat: 'Aucun résultat.',
    aucuneDepeche: 'Aucune dépêche',
    rienFiltres: 'Rien ne correspond aux filtres actifs.',
    tri: 'Tri',
    triRecents: 'Récents',
    triVelocite: 'Vélocité',
    triCritiques: 'Critiques',
    rechercher: 'Rechercher un sujet…',
    type7: 'Type',
    periode: 'Période',
    j7: '7j',
    j30: '30j',
    tout: 'Tout',
    critiquesUniquement: 'Critiques uniquement',
    entites: { one: '{n} entité', other: '{n} entités' },
    filtreSur: 'Filtré sur',
    voirFicheFleche: 'Voir la fiche →',
    deselectionner: 'Désélectionner',
    depechesTitre: 'Dépêches',
    dernieres: 'Dernières nouvelles',
    aucunePourLeMoment: 'Aucune dépêche pour le moment',
    aucuneDesc: 'Aucune actualité ne correspond aux filtres actifs. Élargissez la période ou la criticité.',
  },

  veilleCat: {
    legal_action: 'Judiciaire',
    position_change: 'Repositionnement',
    media_appearance: 'Déclaration',
    contradiction_with_known: 'Contradiction',
    position_vs_action: 'Discours vs actes',
    unexpected_connection: 'Connexion',
    cross_pipeline: 'Croisement',
    temporal: 'Temporel',
    diplomatic: 'Diplomatique',
    new_event: 'Événement',
  },

  veilleCarte: {
    maintenant: 'Ça se passe maintenant',
    toutVoir: 'Tout voir →',
    dernieres: 'Dernières actualités.',
    depeche: 'Dépêche',
    voirFiche: 'Voir la fiche →',
    voir: 'Voir →',
    critique: 'Critique',
    sources: 'Sources',
    replier: 'Replier →',
    lire: 'Lire →',
    statut: {
      confirmed: 'confirmé',
      exploited: 'exploité',
      detected: 'non vérifié',
      dismissed: 'rejeté',
    },
    confianceFaible: 'confiance faible',
    confianceMoy: 'confiance moy.',
    voirFicheDe: 'Voir la fiche {nom} →',
  },

  preds: {
    issue: {
      confirmed: 'Confirmé',
      partially_confirmed: 'Partiellement',
      invalidated: 'Invalidé',
      pending: 'En cours',
    },
    retrospective: '✦ Rétrospective',
    probaInitiale: 'Probabilité initiale :',
    apercuAdmin: '(aperçu admin — analyse non publiée)',
    predictionOrigine: 'Prédiction d’origine ↗',
    issueConstatee: 'Issue constatée ↗',
    vous: 'Vous {n}%',
    nous: 'Nous {n}%',
    parier: 'Parier — puis voir notre estimation',
    connectez: 'Connectez-vous pour parier avant de voir notre estimation →',
    mouvement: 'Mouvement notable (+{n} pts)',
    echeance: '· Échéance {date}',
    voirAnalyse: 'Voir l’analyse →',
    resolues: 'Résolues',
    enCours: 'En cours',
    rechercher: 'Rechercher un scénario…',
    aucunResultat: 'Aucun résultat',
    aucunResolu: 'Aucun scénario résolu ne correspond à cette recherche.',
    aucunEnCours: 'Aucun scénario en cours ne correspond à cette recherche.',
    voirPlus: 'Voir {n} de plus',
    voitJuste: 'Est-ce qu’on voit juste ?',
    calibrationIntro: 'Quand on annonce un chiffre, est-ce qu’il tient ? Sur les scénarios déjà tranchés, voici la part qui s’est vraiment réalisée, selon la probabilité qu’on avait annoncée au départ. L’idéal : ce qu’on donne à 80 % arrive plus souvent que ce qu’on donne à 20 %.',
    annonce: 'Annoncé {tranche}',
    deRealisation: 'de réalisation',
    volumeInsuffisant: 'volume insuffisant',
    pasAssez: 'Pas encore assez de scénarios tranchés pour un bilan fiable ({n} à ce jour) — revenez dans quelques mois.',
    publiees: 'Prédictions publiées',
    confirmees: 'Confirmées',
    partiellement: 'Partiellement',
    invalidees: 'Invalidées',
    predictions: 'Prédictions',
    tag: 'MÉMOIRE · PRÉDICTIONS',
    titre: 'Nos prédictions, vérifiées',
    sousTitre: 'On publie des scénarios datés et chiffrés, puis on affiche honnêtement ce qu’ils sont devenus — confirmations comme erreurs.',
    methodo: 'Chaque scénario est produit avec une probabilité initiale datée, puis re-vérifié périodiquement par notre pipeline de suivi : la probabilité évolue à mesure que de nouveaux éléments apparaissent, jusqu’à un verdict final (confirmé, partiellement confirmé ou invalidé).',
    voirMethodo: 'Voir la méthodologie complète →',
    aucunePubliee: 'Aucune prédiction publiée pour l’instant',
    aucunePublieeDesc: 'Les scénarios apparaîtront ici dès qu’une analyse publiée en contiendra.',
  },

  pari: {
    erreurEnregistrement: 'Le pari n’a pas pu être enregistré.',
    erreurReseau: 'Erreur réseau — réessayez.',
    echauffementTitre: 'Échauffement · rejouez l’histoire',
    votrePari: 'Votre pari',
    fermer: 'Fermer',
    horizon: 'Horizon : {texte}',
    echeance: 'Échéance : {date}',
    votreEstimation: 'Votre estimation',
    deChances: 'de chances que ce scénario se réalise',
    estimationAria: 'Votre estimation en pourcentage',
    impossible: 'Impossible',
    certain: 'Certain',
    cachee: 'Notre estimation reste cachée jusqu’à votre engagement — c’est ce qui rend le pari honnête.',
    engagerDecouvrir: 'Engager et découvrir l’issue',
    engager: 'Engager mon pari',
    vousMieux: 'Vous avez vu plus juste que nous.',
    nousMieux: 'Nous avons vu plus juste cette fois',
    egalite: 'Au coude-à-coude',
    vous: 'Vous',
    nous: 'Nous',
    cePasse: 'Ce qui s’est passé',
    votreScore: 'Votre score',
    leNotre: 'Le nôtre',
    explicationScore: 'Votre score : plus il est bas, plus vous aviez vu juste. L’échauffement porte sur des scénarios déjà connus — il ne compte pas dans votre bilan réel, il sert juste à s’entraîner.',
    enregistre: 'Pari enregistré. On saura qui avait raison quand le scénario se résoudra{echeance}. Votre bilan se mettra à jour automatiquement.',
    echeanceParenthese: ' (échéance {date})',
    continuer: 'Continuer',
    echauffement: 'Échauffement',
    gammes: 'Faites vos gammes',
    gammesIntro: 'Pariez à l’aveugle sur des scénarios déjà tranchés par l’histoire, puis découvrez aussitôt si vous aviez vu juste. Indicatif — ça ne compte pas dans votre bilan réel, mais c’est le meilleur moyen de se faire la main.',
    parierAveugle: 'Parier à l’aveugle →',
    dejaRejoues: 'Déjà rejoués',
    vousN: 'Vous {n}%',
    nousN: 'Nous {n}%',
    voyezJuste: 'Et vous, vous voyez juste ?',
    surVosParis: { one: 'Sur votre {n} pari en direct déjà tranché, voici votre score face au nôtre, sur exactement le même scénario. Plus il est bas, plus vous avez vu juste.', other: 'Sur vos {n} paris en direct déjà tranchés, voici votre score moyen face au nôtre, sur exactement les mêmes scénarios. Plus il est bas, plus vous avez vu juste.' },
    battez: 'Vous battez le modèle sur ce périmètre.',
    modeleAvantage: 'Le modèle garde l’avantage — pour l’instant.',
    enAttente: { one: 'Vous avez {n} pari en direct en attente de verdict. Votre score apparaîtra ici dès que ce scénario sera tranché.', other: 'Vous avez {n} paris en direct en attente de verdict. Votre score apparaîtra ici dès qu’un de ces scénarios sera tranché.' },
    echauffementIndicatif: 'Échauffement (indicatif — scénarios déjà connus)',
  },

  login: {
    enAttente: 'Votre compte est en attente de validation. Vous serez contacté prochainement.',
    desactive: 'Ce compte a été désactivé. Contactez l’administrateur.',
    tropTentatives: 'Trop de tentatives de connexion. Réessayez dans une quinzaine de minutes.',
    incorrect: 'Email ou mot de passe incorrect.',
    erreurCreation: 'Erreur lors de la création du compte.',
    erreurReseau: 'Erreur réseau.',
    devise: 'Comprendre pour ne pas subir.',
    promesse: 'Des faits sourcés, des contradictions documentées, des réseaux cartographiés. Vos conclusions.',
    parolesTitre: 'Paroles et actes',
    parolesTexte: 'les engagements confrontés aux actes',
    rayonxTexte: 'des affirmations vérifiées, preuves à l’appui',
    reseauxTitre: 'Réseaux',
    reseauxTexte: 'qui siège où, et comment on le sait',
    inscription: 'Inscription',
    connexion: 'Connexion',
    demandeEnregistree: 'Demande enregistrée',
    seraActive: 'Votre compte sera activé après validation. Vous serez contacté dès qu’il sera ouvert.',
    retourConnexion: 'Retour à la connexion',
    bonRetour: 'Bon retour.',
    ouvrirCompte: 'Ouvrir un compte.',
    sousConnexion: 'Ce que vous suivez, et ce qui a bougé depuis votre dernière visite.',
    sousInscription: 'Les comptes sont ouverts après validation : vous serez contacté.',
    nom: 'Nom',
    votreNom: 'Votre nom',
    email: 'Email',
    emailExemple: 'votre@email.com',
    motDePasse: 'Mot de passe',
    verification: 'Vérification…',
    entrer: 'Entrer →',
    demanderCompte: 'Demander un compte →',
  },

  qg: {
    gratuit: 'Gratuit',
    monQg: 'Mon QG',
    titre: 'Ce que vous suivez, ce qui bouge.',
    elementsSuivis: { one: 'élément suivi', other: 'éléments suivis' },
    votes: { one: '{n} vote', other: '{n} votes' },
    groupes: { one: '{n} groupe', other: '{n} groupes' },
    intro: 'Vos analyses, acteurs et organisations sauvegardés, et ce qui a bougé depuis votre dernière visite. Et les prochains sujets : proposez, votez — les plus votés deviennent prioritaires.',
  },

  agora: {
    retirerVote: 'Retirer mon vote',
    voter: 'Voter',
    supprimerSuggestion: 'Supprimer ma suggestion',
    confirmerSuppression: 'Supprimer cette suggestion ? Cette action est définitive.',
    suppressionImpossible: "D'autres personnes ont voté pour ce sujet : il ne peut plus être supprimé.",
    pipelineInconnu: 'Pipeline non reconnu.',
    erreurStatut: 'Erreur {n}',
    pipelineLance: 'Pipeline lancé avec succès.',
    erreurReseau: 'Erreur réseau, veuillez réessayer.',
    prochainsSujets: 'Les prochains sujets',
    proposezVotez: 'Proposez et votez — les plus votés passent en priorité.',
    proposer: '+ Proposer',
    enCoursLancement: 'Pipeline en cours de lancement…',
    voirAnalyse: 'Voir l’analyse →',
    affirmationAVerifier: 'Affirmation à vérifier *',
    titreSujet: 'Titre du sujet *',
    pertinent: 'Pourquoi ce sujet est-il pertinent ?',
    region: 'Région',
    domaine: 'Domaine',
    optGeo: 'Analyse Géopolitique',
    optInvestigation: 'Enquête Acteur',
    optBs: 'Cohérence Acteur',
    optOrg: 'Enquête Organisation',
    optRayonx: 'Affirmation au RayonX',
    annuler: 'Annuler',
    envoi: 'Envoi...',
    soumettre: 'Soumettre',
    lancement: 'Lancement…',
    executer: 'Exécuter maintenant',
    aucuneProposition: 'Aucune proposition pour l’instant',
    soyezPremier: 'Soyez le premier à proposer un sujet',
    probables: 'Prochains sujets probables',
    plusVotes: 'les plus votés',
    mesPropositions: 'Mes propositions',
    pasEncore: 'Vous n’avez pas encore proposé de sujet.',
    communaute: 'La communauté propose',
  },

  portfolio: {
    titre: 'Ce que vous suivez',
    nouveautes: { one: '{n} nouveauté', other: '{n} nouveautés' },
    elements: { one: '{n} élément', other: '{n} éléments' },
    tout: 'Tout',
    groupe: '+ Groupe',
    renommer: '✎ Renommer le groupe',
    supprimer: '🗑 Supprimer',
    nomGroupe: 'Nom du groupe...',
    creer: 'Créer',
    annuler: 'Annuler',
    confirmerGroupe: 'Supprimer ce groupe ? Les éléments sauvegardés resteront dans votre portfolio sans groupe.',
    supprimerBtn: 'Supprimer',
    aucun: 'Aucun élément sauvegardé',
    aucunAide: 'Sauvegardez analyses, acteurs et organisations via le bouton 🔖 sur leurs pages',
    voirVigie: 'Voir la vigie de cet item',
    vigie: 'Vigie',
    deplacer: 'Déplacer',
    retirer: 'Retirer du portfolio',
    deplacerVers: 'Déplacer vers',
    sansGroupe: 'Sans groupe',
  },

  vigie: {
    nouveau: 'Nouveau',
    critique: 'Critique',
    maVigie: 'Ma vigie',
    depeches90: { one: '{n} dépêche · 90 derniers jours', other: '{n} dépêches · 90 derniers jours' },
    fermer: 'Fermer',
    ficheComplete: 'Voir la fiche complète →',
    aucune: 'Aucune actualité récente sur cette entité.',
    voirPlus: { one: 'Voir plus ({n} restant)', other: 'Voir plus ({n} restants)' },
  },

  graphe: {
    liens: {
      relationship: 'Relations',
      membership: 'Adhésions',
      co_membership: 'Co-appartenances',
      org_link: 'Liens inter-orgas',
      investigation: 'Investigations',
      event: 'Événements',
    },
    erreurChargement: 'Erreur de chargement',
    aucunChemin: 'Aucun chemin entre « {a} » et « {b} » dans le graphe chargé. Déployez d’autres nœuds d’abord.',
    coSiege: '{n} personnes ont siégé en même temps que ce sujet, sur des années qui se recouvrent. ',
    coCran0: 'Les {montrees} liens les plus forts sont affichés — cliquer pour voir les {n}.',
    coCran1: 'Les {n} sont affichées — cliquer pour revenir aux {plafond} plus fortes.',
    coCran2: 'Les {plafond} plus fortes sont affichées — cliquer pour les masquer.',
    coCran3: 'Masquées — cliquer pour les réafficher.',
    connexionsPremium: '{n} connexions d’investigation — Premium',
    membresNonAffiches: '+{n} membres non affichés',
    rechercher: 'Rechercher une entité…',
    depart: 'Cliquez sur le nœud de départ',
    arrivee: 'De : {nom} → cliquez sur l’arrivée',
    chemin: 'Chemin : {noeuds} nœuds · {liens} liens',
    annuler: '✕ Annuler',
    trouverChemin: '⇢ Trouver un chemin',
    personne: 'Personne',
    organisation: 'Organisation',
    sansFiche: 'Sans fiche',
    aide: 'Clic : détails · Double-clic : déployer · Molette : zoom · Glisser : déplacer',
    construction: 'Construction du graphe…',
    fermer: 'Fermer',
    acteur: 'Acteur',
    entiteSansFiche: 'Entité sans fiche',
    clique: ' · cliqué dans le graphe',
    parolesActes: 'Paroles et actes · {note}/10',
    investigation: 'Investigation',
    liensDansGraphe: 'Liens dans le graphe',
    ficheComplete: 'Voir la fiche complète →',
    deployer: 'Déployer son réseau',
    recentrer: 'Recentrer le graphe ici',
    citeeSansFiche: 'Cette entité est citée par une investigation mais n’a pas encore de fiche dans le socle.',
    leLien: 'Le lien · {type}',
    // Textes fabriqués par /api/graph (13/10/2026).
    api: {
      relation: 'relation',
      membre: 'membre',
      lien: 'lien',
      aSiegeAvec: 'a siégé avec',
      cerclesCommuns: { one: '{n} cercle commun', other: '{n} cercles communs' },
      evenements: { one: '{n} événement', other: '{n} événements' },
      presence: 'Présence documentée — dernier : {nom}',
      membresPartages: { one: '{n} membre partagé', other: '{n} membres partagés' },
      depuis: 'depuis {annee}',
      enAnnee: 'en {annee}',
      enCours: 'en cours',
      force: 'force {n}/10',
    },
    force: 'force {n}/10',
    etabli: 'Ce qui est établi',
    selonFiche: ' · selon la fiche de {nom}',
    pasDescription: 'Pas de description détaillée pour ce lien.',
    source: 'Source',
    voirSource: 'Voir la source',
    socle: {
      organisations: 'Socle Lumière Noire (pipeline organisations).',
      investigation: 'Socle Lumière Noire (pipeline investigation).',
      veille: 'Socle Lumière Noire (pipeline veille/analyse).',
    },
    titre: 'Le Graphe',
    metaDescription: 'Qui est lié à qui, et comment on le sait — exploration interactive du réseau d’influence.',
    tag: 'RÉSEAU · EXPLORATION',
    sousTitre: 'Qui est lié à qui — et surtout, comment on le sait. Chaque lien est cliquable et montre sa source.',
    aucuneEntite: 'Aucune entité dans le socle pour l’instant.',
    provenance: 'Les liens proviennent du socle Lumière Noire : relations documentées par la veille et les analyses, adhésions aux organisations, liens inter-organisations et connexions révélées par les investigations (réservées aux abonnés). Cliquez sur un lien pour voir sa justification et sa source.',
  },

  indicateur: {
    calcul: 'Calcul',
    seuils: 'Seuils',
    composantes: 'Composantes',
    limites: 'Limites',
    enSavoirPlus: 'En savoir plus sur {nom}',
  },

  rxPoints: {
    exact: 'Ce qui est exact',
    inexact: 'Ce qui est inexact ou absent',
  },

  credits: {
    domainePublic: 'Domaine public',
    metaTitre: 'Crédits photo — Lumière Noire',
    metaDescription: 'Auteurs et licences des portraits et logos affichés sur Lumière Noire, issus de Wikimedia Commons et de Wikipédia.',
    fichier: 'fichier ↗',
    licence: 'licence ↗',
    kicker: 'Informations légales',
    titre: 'Crédits photo',
    intro: 'Les {portraits} portraits et {logos} logos du site proviennent de Wikimedia Commons et de Wikipédia. Chacun est publié par son auteur sous la licence indiquée ; les portraits peuvent être recadrés ou affichés en bichromie. Un oubli, une erreur : la page Corrections & droit de réponse est faite pour ça.',
    portraits: 'Portraits ({n})',
    logos: 'Logos ({n})',
  },

  page404: {
    titre: 'Page introuvable',
    erreur: 'Erreur 404',
    rien: 'La lumière n’a rien révélé ici.',
    explication: 'Cette page n’existe pas, ou plus. Le lien a peut-être changé, ou le contenu n’a pas encore été publié. Reprenons la piste depuis un point sûr.',
    brief: 'Le Brief du jour →',
    accueil: 'Accueil',
    analyses: 'Analyses',
    chercherDesktop: 'Vous cherchiez quelque chose de précis ? Utilisez la recherche',
    chercherMobile: 'Vous cherchiez quelque chose de précis ? Utilisez la loupe en haut de page.',
  },

  site: {
    titreDefaut: 'Lumière Noire — Comprendre pour ne pas subir',
    description: 'Analyses géopolitiques indépendantes. Investigations, scores de cohérence, décryptages du pouvoir réel.',
  },

  apropos: {
    titre: 'À propos',
    metaDescription: 'Pourquoi Lumière Noire existe. L’histoire derrière le projet, sa mission, et ce qui le distingue.',
    ogDescription: 'Entre les médias qui répètent et les sites qui complotent, il manquait un espace rigoureux.',
    devise: 'Comprendre pour ne pas subir.',
    chapo: 'Entre les médias qui tournent en boucle et les sites qui voient des complots partout, il manquait quelque chose de rigoureux. Lumière Noire est née de cette frustration.',
    histoireTitre: 'Comment tout a commencé',
    histoire1: 'Avant le COVID, je ne suivais pas l’actualité de manière intensive. Comme beaucoup, j’ai eu peur au début de la pandémie et j’ai suivi les consignes des autorités. C’était la chose logique à faire.',
    histoire2: 'Puis est arrivé le variant Omicron. Très contagieux, peu dangereux. Je me suis dit : parfait — on va tous attraper ça, on aura l’immunité, les restrictions vont être levées. C’était la logique sanitaire évidente.',
    histoire3: 'C’est là que le passe sanitaire a été introduit. En dépit de toute logique. Les consignes changeaient tous les jours, dans tous les sens, sans jamais reconnaître la moindre erreur. Les conseils scientifiques fonctionnaient dans une opacité totale. Des médecins qui exprimaient des positions différentes étaient sanctionnés ou invisibilisés. Et les médias mainstream répétaient en boucle, sans aucune pensée critique.',
    histoire4: 'Ce moment m’a ouvert les yeux. Pas pour me faire basculer vers les théories du complot — l’inverse. Je cherchais quelque chose de rigoureux, de sourcé, de mesuré. Quelque chose qui ne prenne pas parti mais qui documente, qui questionne, qui refuse d’enterrer les sujets sous l’écume de l’actualité suivante.',
    histoire5: 'Je voyais aussi comment aucune responsabilité n’était jamais engagée pour les puissants. Comment les affaires importantes se noyaient dans le flux incessant de nouvelles. Comment un sujet qui « aurait dû tout changer » disparaissait en quelques semaines, sans suite, sans mémoire.',
    histoire6: 'Lumière Noire est née de tout ça.',
    pasTitre: 'Ce que nous ne sommes pas',
    pasMainstream: 'Pas un média mainstream',
    pasMainstreamTexte: 'Nous ne répétons pas les communiqués officiels. Nous ne suivons pas la hype de l’actualité. Nous ne faisons pas de boucle.',
    pasComplot: 'Pas un site complotiste',
    pasComplotTexte: 'Nous ne faisons pas de révélations sans preuves. Nous ne tirons pas de conclusions au-delà de ce que les sources permettent. Nous disons « on ne sait pas » quand on ne sait pas.',
    pasPartisan: 'Pas un organe partisan',
    pasPartisanTexte: 'Nous analysons les actes, pas les camps. Un score de cohérence faible est un score de cohérence faible, qu’il s’agisse de la gauche ou de la droite.',
    pasVerite: 'Pas une vérité imposée',
    pasVeriteTexte: 'Notre travail est de vous donner les éléments. Les conclusions, c’est votre affaire.',
    faisonsTitre: 'Ce que nous faisons',
    ecartsGras: 'Nous mesurons les écarts entre les discours et les actes.',
    ecartsTexte: 'L’analyse de cohérence documente les engagements pris publiquement, les classe, les évalue engagement par engagement avec les sources à l’appui. Le résultat est un score — mais surtout une liste de contradictions documentées.',
    memoireGras: 'Nous gardons la mémoire.',
    memoireTexte: 'L’actualité compte sur votre oubli. Ce qui a été dit en 2019 et contredit en 2023 — nous le retrouvons, le documentons, le relions. Les fils rouges et les dossiers sont là pour ça.',
    perspectivesGras: 'Nous construisons des analyses multi-perspectives.',
    perspectivesTexte: 'Sur les sujets géopolitiques complexes, nous ne choisissons pas une interprétation et n’enterrons pas les autres. Nous présentons les hypothèses concurrentes, leurs arguments, leurs limites.',
    reseauxGras: 'Nous cartographions les réseaux.',
    reseauxTexte: 'Qui finance quoi, qui connaît qui, quelles organisations influencent quelles décisions — et à quoi ça mène concrètement.',
    projetTitre: 'Un projet indépendant',
    projet1: 'Lumière Noire est un projet indépendant, sans actionnaire, sans annonceur, sans ligne éditoriale dictée par un financeur. Ce qui implique des moyens limités — et donc des choix : approfondir plutôt qu’accélérer, documenter plutôt que commenter.',
    projet2: 'Les analyses sont produites avec l’aide de l’IA — pas uniquement pour traiter le volume de données qu’un humain seul ne pourrait pas couvrir, mais aussi pour une raison plus fondamentale : faire en sorte que les résultats ne soient pas pollués par mon affect ou mes convictions personnelles. Sur des sujets politiques, il est difficile d’être totalement neutre. L’IA n’a pas d’opinions sur Macron ou Mélenchon. Elle applique le même protocole à tout le monde. C’est ça que je cherchais.',
    projet3: 'C’est la même logique qui guide le système de votes et de soumissions de sujets. Si je choisissais seul les acteurs et les sujets analysés, la plateforme refléterait inévitablement mes centres d’intérêt et mes angles morts. Permettre aux utilisateurs de proposer et de voter, c’est s’assurer que Lumière Noire suit ce qui vous préoccupe — pas ce qui me préoccupe moi.',
    projet4: 'Chaque résultat est contrôlé, chaque source est vérifiée, chaque score est justifié. Mais la direction du regard, elle, n’appartient pas qu’à moi.',
    projet5: 'Si ce projet vous parle, la meilleure façon de le soutenir est de l’utiliser, d’en parler, et si vous le souhaitez, de prendre un abonnement qui nous permet de continuer à le faire exister.',
    explorer: 'Explorer la plateforme',
    explorerTexte: 'Accès libre aux analyses, profils d’acteurs, vérifications de faits et cartographies d’organisations. Un compte gratuit débloque Mon QG : votes sur les sujets prioritaires, soumission de propositions, création de dossiers et bookmarks.',
    accueil: 'Accueil →',
    methodologie: 'Notre méthodologie',
  },

  couvEdito: {
    pageAccroche: 'Page d’accroche',
    lire: 'Lire la page d’accroche →',
    replier: '← Replier',
  },

  qgLabels: {
    type: {
      actor: 'Acteur',
      analysis: 'Analyse',
      organization: 'Organisation',
      investigation: 'Investigation',
    },
    pipeline: {
      geo: 'Géopolitique',
      investigation: 'Investigation',
      bs: 'Analyse de cohérence',
      org: 'Organisation',
      rayonx: 'RayonX',
    },
    statut: {
      pending: 'En attente',
      accepted: 'Accepté',
      in_progress: 'En cours',
      published: 'Publié',
      rejected: 'Refusé',
    },
  },

  secteurs: {
    political: 'Politique',
    corporate: 'Entreprises',
    financial: 'Finance',
    media: 'Médias',
    academic: 'Université',
    internationalOrg: 'Org. internationales',
    military: 'Armée',
    autres: 'Autres',
  },

  historique: {
    create_position: 'Position ajoutée à la fiche',
    create_flow: 'Flux financier documenté',
  },

  // L'accueil de la nouvelle présentation (AccueilConstats, 2/11/2026).
  une: {
    titre: 'Lumière Noire – Comprendre pour ne pas subir',
    presentation: "Lumière Noire confronte ce que disent les responsables publics aux faits : promesses tenues ou non, affirmations vérifiées, scénarios des crises en cours, réseaux d'influence. Chaque information renvoie à sa source.",
    pourcent: '{p} %',
    glisser: 'glisser',
    aucunePublication: "Aucune publication pour l'instant.",
    contexte: 'Contexte',
    analyses: {
      titre: 'Analyses',
      texte: 'Ce qui est établi, ce qui est contesté, les perspectives en concurrence et les issues probables.',
      toutes: { one: "L'analyse →", other: 'Les {n} analyses →' },
      miseAJour: 'Analyse · mise à jour le {date}',
      publiee: 'Analyse · publiée le {date}',
      evolueLe: 'Ce qui a évolué le {date}',
      scenariosChiffres: 'Nos scénarios chiffrés, révisés quand les faits changent.',
      plusProbable: 'Le scénario le plus probable',
      plusProbableDeux: 'Scénario le plus probable :',
      dIciLe: ", d'ici le {date}",
      lesScenarios: 'Les scénarios →',
      lire: "Lire l'analyse →",
      etabli: 'Établi',
      conteste: 'Contesté',
      perspectives: 'Les perspectives en concurrence',
      faits: { one: 'fait établi', other: 'faits établis' },
      nPerspectives: { one: 'perspective', other: 'perspectives' },
      sources: { one: 'source', other: 'sources' },
    },
    promesses: {
      titre: 'Promesses tenues ?',
      texte: "Ce qu'ils ont dit, ce qu'ils ont fait.",
      tous: { one: 'Le baromètre →', other: 'Les {n} baromètres →' },
      leBarometre: 'Le baromètre →',
      voirTous: 'Voir les {n} engagements →',
      voirTousUnParUn: 'Voir les {n} engagements, un par un →',
      lesN: 'Les {n} engagements →',
      misAJour: 'Mis à jour le {date}',
      nonTenus: { one: 'non tenu', other: 'non tenus' },
      partiels: { one: 'en partie', other: 'en partie' },
      tenus: { one: 'tenu', other: 'tenus' },
      sansNote: 'sans note',
      sansExecution: "sans note d'exécution",
    },
    // La phrase du bilan d'un baromètre (phraseBilan, src/lib/accueil/blocs.ts).
    bilan: {
      aucunNote: { one: '{n} engagement, aucun encore noté en exécution.', other: '{n} engagements, aucun encore noté en exécution.' },
      nonTenus: { one: '{n} engagement sur {total} non tenu', other: '{n} engagements sur {total} non tenus' },
      partiels: '{n} en partie',
      aucunTenu: 'aucun tenu',
      tenus: { one: '{n} tenu', other: '{n} tenus' },
      sans: " ; {n} sans note d'exécution",
      separateur: ', ',
      fin: '.',
    },
    depuis: {
      titre: 'Depuis votre dernière visite, le {date}',
      rien: 'Rien de neuf depuis : aucune publication, aucun scénario révisé, aucune dépêche critique.',
      parus: 'Paru depuis · {n}',
      aEvolue: 'A évolué · {sujet}, le {date}',
      pourquoi: 'Pourquoi ça a évolué →',
      critiques: { one: 'Dans la veille · {n} critique', other: 'Dans la veille · {n} critiques' },
      veilleDepuis: 'La veille depuis le {date} →',
    },
    actualite: {
      titre: 'L’actualité',
      aVenir: 'À venir',
      derniersJours: 'Ces derniers jours',
      toute: 'Toute la veille →',
    },
    dernieresPublications: 'Dernières publications',
    avis: {
      titre: 'À votre avis ?',
      vrai: 'Vrai',
      faux: 'Faux',
      detail: 'Le détail →',
    },
    explorer: {
      titre: 'Tout explorer',
      promesses: {
        titre: 'Promesses tenues ?',
        texte: "Ce qu'ils ont dit, ce qu'ils ont fait.",
        compte: { one: '{n} baromètre', other: '{n} baromètres' },
      },
      analyses: {
        titre: 'Analyses et scénarios',
        texte: 'Les crises en cours et ce qui peut arriver, chiffré et révisé.',
        court: 'Les crises en cours et ce qui peut arriver.',
        compte: { one: '{n} analyse', other: '{n} analyses' },
      },
      rayonx: {
        titre: 'RayonX',
        texte: 'Une affirmation qui circule, vérifiée phrase par phrase.',
        court: 'Une affirmation vérifiée phrase par phrase.',
        compte: { one: '{n} affirmation', other: '{n} affirmations' },
        tampon: 'VRAI ?',
      },
      reseaux: {
        titre: 'Réseaux',
        texte: 'Qui siège où, qui finance qui : des liens datés et sourcés.',
        court: 'Qui siège où, qui finance qui.',
        compte: '{acteurs} personnalités · {orgs} organisations',
      },
      enquetes: {
        titre: 'Enquêtes',
        texte: "Les dossiers qu'on creuse au-delà de l'actualité.",
        court: 'Loupe, anatomies, investigations.',
        compte: 'Loupe, anatomies, investigations',
        derniereLoupe: 'Dernière Loupe : {titre}.',
      },
    },
  },

} as const;
